import { useQuery } from '@tanstack/react-query';
import {
  Award,
  Check,
  Clock,
  CreditCard,
  Gift,
  Headphones,
  Heart,
  Leaf,
  Package,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  Star,
  Truck,
  type LucideIcon,
} from 'lucide-react';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { Link, UNSAFE_LocationContext, useInRouterContext } from 'react-router';

import {
  sendContactMessage,
  subscribeToNewsletter,
  type FacetValue,
  type StorefrontCategory,
  type StorefrontProductSummary,
} from '@/features/storefront/api/storefront';
import { areaStyle, colorScope, resolveColor } from '@/features/storefront/appearance';
import { BAND_CARD } from '@/features/storefront/bandFrame';
import { StorefrontThemeContext } from '@/features/storefront/storefrontThemeContext';
import { SectionEdge } from '@/features/storefront/sections/SectionEdge';
import { useShopContent } from '@/features/storefront/content/useContent';
import { focalStyle } from '@/features/storefront/focal';
import { useStorefrontPaths } from '@/features/storefront/paths';
import { useStorefrontLanguage } from '@/features/storefront/i18n/context';
import { LanguageSwitcher } from '@/features/storefront/i18n/LanguageSwitcher';
import { useThemeLanguageSwitcher } from '@/features/storefront/i18n/switcher';
import { BookingCalendar } from '@/features/storefront/sections/BookingCalendar';
import { BookingWizard } from '@/features/storefront/sections/BookingWizard';
import { serviceFromSearch } from '@/features/storefront/sections/team';
import { EditableRichText } from '@/features/storefront/sections/EditableRichText';
import { EditableText } from '@/features/storefront/sections/EditableText';
import { ProductReviews } from '@/features/storefront/sections/ProductReviews';
import { useStorefrontAccountData, useStorefrontData } from '@/features/storefront/data/context';
import { useRecentlyViewedProducts } from '@/features/storefront/wishlist/useRecentlyViewedProducts';
import { BlogPostsView } from '@/features/storefront/sections/BlogPostsSection';
import {
  EMPTY_TEASER,
  teaserPosts,
  type BlogTeaser,
} from '@/features/storefront/sections/blogTeaser';
import { useStorefrontFeatures } from '@/features/storefront/features';
import { CountdownView } from '@/features/storefront/sections/CountdownSection';
import { StoreLocatorView } from '@/features/storefront/sections/StoreLocatorSection';
import type { StorefrontLocations } from '@/features/storefront/api/locations';
import type { StorefrontTeam } from '@/features/storefront/api/team';
import { TeamView } from '@/features/storefront/sections/TeamSection';
import type { StorefrontCareers } from '@/features/storefront/api/careers';
import { CareersView } from '@/features/storefront/sections/CareersSection';
import { LinkDirectoryView, linkDirectoryGroups } from '@/features/storefront/sections/LinkDirectorySection';
import { CategoryStripView, categoryStripItems } from '@/features/storefront/sections/CategoryStripSection';
import {
  FetchedLookbook,
  LookbookView,
  lookbookProductFromSummary,
  type LookbookProduct,
  type ProductDetailQueryFactory,
} from '@/features/storefront/sections/LookbookSection';
import { BeforeAfterCards, BeforeAfterView, hasBeforeAfterPhoto } from '@/features/storefront/sections/BeforeAfterSection';
import { PackageBuyButton } from '@/features/storefront/sections/PackageBuyButton';
import { PricingPlansView, pricingPlans } from '@/features/storefront/sections/PricingPlansSection';
import { BmiCalculatorView } from '@/features/storefront/sections/BmiCalculatorSection';
import { MarqueeView, marqueeItems } from '@/features/storefront/sections/MarqueeSection';
import {
  FetchedProductSpotlight,
  ProductSpotlightView,
  spotlightFromSummary,
  spotlightSlug,
} from '@/features/storefront/sections/ProductSpotlightSection';
import {
  FetchedProductShowcase,
  ProductShowcaseCarousel,
  ProductShowcaseView,
  ShowcasePills,
  showcasePillCategories,
} from '@/features/storefront/sections/ProductShowcaseSection';
import { ProductTabList, useProductTabs } from '@/features/storefront/sections/ProductTabsSection';
import {
  productTabId,
  productTabPanelId,
  type ProductQueryFactory,
} from '@/features/storefront/sections/productTabs';
import { parseEndsAt } from '@/features/storefront/sections/countdown';
import { type CardPresentation, ProductCard } from '@/features/storefront/components/ProductCard';
import { RAIL_ITEM, Rail } from '@/features/storefront/components/Rail';
import { AboveTheFold, StorefrontImage, usePreloadNext } from '@/features/storefront/components/StorefrontImage';
import { StorefrontContainer as Container } from '@/features/storefront/components/StorefrontContainer';
import { contentWidthLength } from '@/features/storefront/components/containerWidth';
import type {
  AnchorSection,
  AnchorSectionType,
  BannerHeight,
  Block,
  SectionDevice,
  SectionPadding,
  BannerSection,
  BookingsSection,
  StoreLocatorSection,
  CareersSection,
  TeamSection,
  CategoriesSection,
  CategoryStripSection,
  ContactFormSection,
  FaqSection,
  FeatureIcon,
  FeaturedProductsSection,
  RecentlyViewedSection,
  ProductTabBlock,
  ProductTabsSection,
  FeaturesSection,
  GallerySection,
  PromoGridLayout,
  PromoGridSection,
  PromoTileBlock,
  BrandStripSection,
  LookbookSection,
  BeforeAfterSection,
  PricingPlansSection,
  ProductSpotlightSection,
  ProductShowcaseSection,
  HeroSection,
  ImageWithTextSection,
  LanguageSelectorSection,
  NewsletterSection,
  CountdownSection,
  BmiCalculatorSection,
  MarqueeSection,
  BlogPostsSection,
  ProductReviewsSection,
  Section,
  SlideshowSection,
  SocialLinkBlock,
  SocialLinksSection,
  TestimonialsSection,
  TextSection,
  LinkDirectorySection,
  TimelineSection,
  VideoSection,
} from '@/features/storefront/sections/schema';
import { SocialIcon } from '@/features/storefront/sections/SocialIcon';
import {
  SOCIAL_ICON_PIXELS,
  SOCIAL_PLATFORM_COLORS,
  socialLinkHref,
  socialLinkLabel,
} from '@/features/storefront/sections/social';
import {
  VIDEO_ASPECT_CSS,
  type VideoOverlayPosition,
  youtubeEmbedUrl,
  youtubeVideoId,
} from '@/features/storefront/sections/video';
import {
  isPlaceholderId,
  placeholderCategories,
  placeholderProducts,
  placeholderTeam,
  withPlaceholderContent,
} from '@/features/storefront/sections/placeholderContent';
import {
  contentWidthFor,
  heroImageItems,
  heroImages,
  isAnchor,
  isPlaceholderHero,
  safeHref,
  sectionPadding,
  sectionRows,
  sectionSpan,
  pickedBlocks,
  shownBlocks,
  PROMO_LAYOUT_TILES,
  type SectionSpan,
} from '@/features/storefront/sections/schema';
import {
  STOREFRONT_BUTTON_STYLE,
  STOREFRONT_HEADING_STYLE,
  STOREFRONT_TILE_IMAGE,
  tileImageFit,
} from '@/features/storefront/theme';
import { cn } from '@/lib/cn';
import { contrastRatio, isHexColor } from '@/lib/color';
import { useColorMode } from '@/features/storefront/colorMode/ColorModeContext';
import { flipColors } from '@/features/storefront/colorMode/palette';
import { formatMoney } from '@/lib/money';
import { gridImageSizes } from '@/lib/imageRenditions';
import { sanitizeRichText } from '@/lib/richText';

/**
 * The home page, rendered from the theme's sections.
 *
 * One renderer, two callers: the shopper's storefront and the dashboard's
 * customiser preview. Everything a section needs beyond its own settings -
 * products, categories, whether links navigate - arrives through
 * `SectionEnvironment`, so neither caller has to reimplement a section to show
 * it.
 */
export interface SectionEnvironment {
  storeName: string;
  /**
   * The products for one product section, by the category it is pinned to
   * (`null` for "all"). A lookup rather than one list to filter, because
   * filtering a single page of products client-side reports an empty category
   * whenever its items happen to fall past the end of that page - which is a
   * lie about the catalog, told most often to the largest shops.
   */
  productsFor: (categoryId: string | null) => StorefrontProductSummary[];
  /**
   * How a product tab asks the shop for its products - newest, best selling,
   * on sale or one category - so each tab fetches only when it is opened.
   * Left off, a tab row answers from `productsFor` instead (see `ProductTabs`).
   */
  productQuery?: ProductQueryFactory;
  categories: StorefrontCategory[];
  /**
   * True inside the dashboard. Links become plain text and forms do not submit:
   * a merchant clicking their own hero button while designing must not be
   * thrown out of the customiser, and the signup box must not subscribe them.
   */
  preview: boolean;
  /** Where a product card points. Written shopper-side (`/products/<slug>`). */
  productHref: (slug: string) => string;
  /** Set while products or categories are still arriving, for placeholder cards. */
  loading?: boolean;
  /**
   * The product the page is about, on the one page that has one.
   *
   * What a reviews section reviews. Unset everywhere but the shopper's product
   * page, and a section that needs a product renders nothing without one - the
   * customiser draws stand-ins instead, so it has no need of it.
   */
  product?: { slug: string; name: string } | null;
  /**
   * What each page's own content renders as.
   *
   * An anchor section - the cart on the cart page, the product on the product
   * page - has no markup of its own here. It is a position in the merchant's
   * stack, and the page that owns the content fills it: the shopper's cart
   * page hands over its real basket, and the customiser hands over a
   * stand-in. That is what keeps the cart's logic in the cart page while the
   * merchant still decides what stands above and below it.
   *
   * An anchor whose slot is not filled renders nothing at all, which is the
   * right answer for the one caller that can produce it - a preview of a page
   * whose content it does not have.
   */
  slots?: Partial<Record<AnchorSectionType, ReactNode>>;
  /**
   * Which screen is being drawn, when the caller knows better than the window
   * does.
   *
   * Unset - the shopper's storefront - means "let the breakpoints decide", and
   * the responsive settings render as media queries. Set - the customiser,
   * whose phone preview is a narrow frame inside a wide window - means resolve
   * them here instead, because a media query would answer for the merchant's
   * monitor rather than for the frame they are looking at.
   */
  device?: SectionDevice;
  /**
   * True in the customiser, where a hidden section or block is dimmed rather
   * than dropped: the merchant has to be able to see what they took down, and
   * where it stood, to put it back.
   */
  includeHidden?: boolean;
  /**
   * Whether a section with nothing in it yet draws a stand-in of what it will
   * look like once it has something.
   *
   * The reason it exists: a page opened in the customiser for the first time is
   * mostly empty, and a merchant looking at eight bands of padding cannot tell
   * a testimonials section from a gallery, let alone judge whether the order is
   * right. Filled in, the same page is a picture of their shop that they can
   * rearrange before writing a word - which is the difference between a theme
   * editor and a website builder.
   *
   * The stand-in is *content*, not markup: see `placeholderContent.ts`. The
   * section renders through the shopper's own renderer either way, so every
   * setting on it applies to the stand-in, and the moment the merchant's real
   * content arrives it takes over with nothing else changing.
   *
   * Off by default and never set on the shopper's side, deliberately. An
   * invented five-star review or a product that does not exist, shown to a
   * shopper, is not a placeholder - it is a false claim about the shop. Opt-in
   * rather than opt-out is what makes that a thing a caller has to ask for
   * rather than a thing a caller has to remember to refuse.
   */
  placeholders?: boolean;
  /**
   * Wraps one block, so the customiser can outline the one being edited.
   *
   * Left off - which is every caller but the customiser - the block renders
   * exactly the markup it always did, with no wrapper element around it. That
   * is what keeps the preview and the shopper's page the same page.
   */
  blockWrap?: (section: Section, block: Block, rendered: ReactNode) => ReactNode;
  /**
   * Whether a piece of copy can be typed into on the page, and where the
   * typing goes.
   *
   * Asked once per editable piece of copy - a hero's heading, a slide's button
   * text, a section's title - with the section as it is being drawn, the name
   * of the setting (`heading`, `cta_label`, ...) and, for copy inside a block,
   * the block's id. Answering `null` (or leaving this off, which every caller
   * but the customiser does) renders the words exactly as before, with no
   * wrapper around them. See `EditableText` and `makeEditTextHook`.
   *
   * Stand-in sections and blocks are never offered here: they are not in the
   * merchant's document, so there is nowhere for the words to be written.
   */
  editText?: EditTextHook;
  /**
   * The brands on the shop's products, from the catalog's facet counts - what
   * a brand strip with no logos of its own shows as wordmarks. Fetched only
   * when such a strip is on the page; unset or empty leaves the strip hidden.
   */
  brands?: FacetValue[];
  /**
   * The newest blog posts, for a `blog_posts` section - see `blogTeaser.ts`.
   * Fetched only when such a section is on the page and the blog is on;
   * unset or empty leaves the section off the live page.
   */
  blogTeaser?: BlogTeaser;
  /**
   * The shop's active branches, for a `store_locator` section. Fetched only
   * when such a section is on the page; `null` while loading. Unset or empty
   * leaves the section off the live page.
   */
  storeLocations?: StorefrontLocations | null;
  /**
   * The shop's visible people, for a `team` section and for a bookings
   * section that books by person. Fetched only when the page has either;
   * `null` while loading. Unset or empty leaves a team section off the live
   * page, and a person picker with nobody to offer.
   */
  team?: StorefrontTeam | null;
  /**
   * The shop's open positions, for a `careers` section. Fetched only when such
   * a section is on the page and the Careers page is on; `null` while
   * loading. Unset or empty leaves the section off the live page.
   */
  careers?: StorefrontCareers | null;
  /**
   * How a lookbook asks the shop for one of its tagged products in full, by
   * slug - see `LookbookSection.tsx`. Left off, a lookbook finds its products
   * among `productsFor(null)` and its cards link without a quick add.
   */
  productDetailQuery?: ProductDetailQueryFactory;
}

/** What `SectionEnvironment.editText` answers for one editable piece of copy. */
export interface EditTextHandle {
  editable: boolean;
  /** The finished value, once - on Enter or on leaving the box. */
  onCommit(next: string): void;
}

export type EditTextHook = (section: Section, field: string, blockId?: string) => EditTextHandle | null;

const EnvironmentContext = createContext<SectionEnvironment | null>(null);

function useEnvironment(): SectionEnvironment {
  const environment = useContext(EnvironmentContext);
  if (!environment) {
    throw new Error('Sections must be rendered inside <SectionList>.');
  }
  return environment;
}

/**
 * One piece of section copy, typeable on the page where the customiser allows
 * it and exactly its children everywhere else.
 *
 * A component rather than a hook so it can stand anywhere in the markup -
 * inside a slide's map, beside a conditional - without bending the rules of
 * hooks around it.
 */
function Editable({
  section,
  field,
  blockId,
  value,
  multiline,
  emphasis,
  isolateClick,
  children,
}: {
  section: Section;
  field: string;
  blockId?: string;
  value: string;
  multiline?: boolean;
  emphasis?: boolean;
  isolateClick?: boolean;
  children: ReactNode;
}) {
  const { editText } = useEnvironment();
  const placeholder = isPlaceholderId(section.id) || (blockId !== undefined && isPlaceholderId(blockId));
  const handle = editText && !placeholder ? editText(section, field, blockId) : null;
  return (
    <EditableText
      handle={handle}
      value={value}
      field={field}
      multiline={multiline}
      emphasis={emphasis}
      isolateClick={isolateClick}
    >
      {children}
    </EditableText>
  );
}

// --- Shared chrome --------------------------------------------------------
// Every step is the classic value (py-6, py-12, …) multiplied by the layout
// family's spacing scale, so an editorial theme breathes wider and a minimal
// one opens up without any section knowing why. At scale 1 these render
// exactly the pixels the plain classes did.
const PADDING: Record<Section['padding'], string> = {
  none: 'py-0',
  sm: 'py-[calc(1.5rem*var(--storefront-space-scale,1))]',
  md: 'py-[calc(3rem*var(--storefront-space-scale,1))]',
  lg: 'py-[calc(4rem*var(--storefront-space-scale,1))] sm:py-[calc(5rem*var(--storefront-space-scale,1))]',
  xl: 'py-[calc(6rem*var(--storefront-space-scale,1))] sm:py-[calc(7rem*var(--storefront-space-scale,1))]',
};

/**
 * The same steps at the two narrower widths, written out so Tailwind can see
 * them and without the internal `sm:` jump the desktop map carries - a section
 * that has been given a tablet padding should hold it across the whole tablet,
 * not grow half way through it.
 *
 * Only emitted for a section whose merchant actually set one. A page nobody
 * has opened the responsive group on renders the single class it always did.
 */
const PADDING_MOBILE: Record<SectionPadding, string> = {
  none: 'py-0',
  sm: 'py-[calc(1.5rem*var(--storefront-space-scale,1))]',
  md: 'py-[calc(3rem*var(--storefront-space-scale,1))]',
  lg: 'py-[calc(4rem*var(--storefront-space-scale,1))]',
  xl: 'py-[calc(6rem*var(--storefront-space-scale,1))]',
};

const PADDING_TABLET: Record<SectionPadding, string> = {
  none: 'md:py-0',
  sm: 'md:py-[calc(1.5rem*var(--storefront-space-scale,1))]',
  md: 'md:py-[calc(3rem*var(--storefront-space-scale,1))]',
  lg: 'md:py-[calc(4.5rem*var(--storefront-space-scale,1))]',
  xl: 'md:py-[calc(6.5rem*var(--storefront-space-scale,1))]',
};

const PADDING_DESKTOP: Record<SectionPadding, string> = {
  none: 'lg:py-0',
  sm: 'lg:py-[calc(1.5rem*var(--storefront-space-scale,1))]',
  md: 'lg:py-[calc(3rem*var(--storefront-space-scale,1))]',
  lg: 'lg:py-[calc(5rem*var(--storefront-space-scale,1))]',
  xl: 'lg:py-[calc(7rem*var(--storefront-space-scale,1))]',
};

/**
 * Where a section left off one screen size disappears, as media queries.
 *
 * Mobile is below the first breakpoint, tablet between the first and the
 * second, desktop above - the same three the customiser's preview switch
 * offers, so what a merchant previews is what a shopper gets.
 */
const HIDDEN_ON: Record<SectionDevice, string> = {
  mobile: 'max-md:hidden',
  tablet: 'md:max-lg:hidden',
  desktop: 'lg:hidden',
};

/** Column counts a section may ask for, pre-resolved so Tailwind can see them. */
const COLUMNS: Record<number, string> = {
  2: 'grid-cols-2',
  3: 'grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-2 lg:grid-cols-4',
  5: 'grid-cols-2 lg:grid-cols-5',
  // Six and seven are for the wide themes only, and they take a middle step:
  // fourteen shelves seven across is a lineup to scan on a 1400px page and an
  // unreadable strip of thumbnails on a tablet.
  6: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-6',
  7: 'grid-cols-2 md:grid-cols-4 lg:grid-cols-7',
  // Eight to ten are dense marketplace / grocery shelves of compact cards
  // and small category tiles: the full count only on a wide desktop, a
  // tighter gutter so the cards keep their width.
  8: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3 lg:gap-4',
  9: 'grid-cols-3 md:grid-cols-5 lg:grid-cols-7 xl:grid-cols-9 gap-3 lg:gap-4',
  10: 'grid-cols-3 md:grid-cols-5 lg:grid-cols-8 xl:grid-cols-10 gap-3 lg:gap-4',
};

function columnClass(columns: number): string {
  return COLUMNS[columns] ?? COLUMNS[4];
}

const FEATURE_ICON_COMPONENTS: Record<FeatureIcon, LucideIcon> = {
  truck: Truck,
  shield: ShieldCheck,
  return: RotateCcw,
  support: Headphones,
  payment: CreditCard,
  sparkles: Sparkles,
  leaf: Leaf,
  award: Award,
  gift: Gift,
  clock: Clock,
  heart: Heart,
  package: Package,
};

/**
 * A link the merchant typed, or inert text in the preview.
 *
 * Off-site links leave the router alone - `<Link to="https://…">` would try to
 * route to it - and in-page anchors stay anchors so the hero's "shop now" can
 * point at the product grid below it.
 */
function SectionLink({
  href: raw,
  className,
  style,
  label,
  newTab = false,
  children,
}: {
  href: string;
  className?: string;
  style?: React.CSSProperties;
  /**
   * What the link is called, for a link whose content does not say.
   *
   * Only the icon links need it - a button carries its own words, and giving
   * one an `aria-label` as well would have a screen reader read the label
   * while a sighted shopper reads the button. An anchor whose only child is an
   * SVG has nothing to read at all, which is the case this exists for.
   */
  label?: string;
  /**
   * Open an off-site link in its own tab.
   *
   * Off by default, because a merchant's "shop now" button belongs in the tab
   * the shopper is already in. The social icons are the exception this exists
   * for: a shopper who taps Instagram halfway through a basket has not asked
   * to leave, and on a phone "back" out of a native app that opened over the
   * browser is not a reliable way back to the shop.
   *
   * Ignored for in-shop links, which stay in the tab whatever is asked - a
   * second tab of the same shop is a second basket to be confused by.
   */
  newTab?: boolean;
  children: ReactNode;
}) {
  const { preview } = useEnvironment();
  const href = safeHref(raw);

  const paths = useStorefrontPaths();

  if (preview || !href) {
    return (
      <span className={className} style={style} title={label}>
        {children}
      </span>
    );
  }

  if (/^https?:\/\//i.test(href) || /^(mailto|tel):/i.test(href) || href.startsWith('#')) {
    return (
      <a
        href={href}
        className={className}
        style={style}
        aria-label={label}
        title={label}
        target={newTab ? '_blank' : undefined}
        // `noreferrer` implies `noopener`, and both matter more with a target:
        // a page opened this way can otherwise reach back through
        // `window.opener` and navigate the shop out from under the shopper.
        rel="noreferrer"
      >
        {children}
      </a>
    );
  }

  // Storefront paths are written the way a shopper sees them ("/products").
  // Which shop they are read against - the merchant's, or a theme's static
  // preview - is what `paths` carries.
  return (
    <Link to={paths.menu(href)} className={className} style={style} aria-label={label} title={label}>
      {children}
    </Link>
  );
}

/**
 * A heading, with the word it leans on in the theme's display face.
 *
 * `'Shop by *Categories*'` renders "Categories" in `--storefront-display-font`
 * and the rest in the heading face. The markers are stripped whichever theme
 * is rendering: a family that declares no display face resolves the variable
 * to `inherit`, so the emphasised word comes out identical to its neighbours
 * and nothing about the heading changes - which is what makes this safe to
 * wrap every heading in rather than only the ones that asked for it.
 *
 * Split rather than parsed: this is emphasis in a heading, not a markup
 * language, and a heading is one line long.
 */
function HeadingText({ children }: { children: string }) {
  return (
    <>
      {children.split('*').map((part, index) =>
        // Odd segments are what stood between a pair of asterisks.
        index % 2 === 1 ? (
          <span key={index} style={{ fontFamily: 'var(--storefront-display-font)' }}>
            {part}
          </span>
        ) : (
          part
        ),
      )}
    </>
  );
}

/**
 * The heading over a section, aligned as the layout family stands it.
 *
 * Both halves of "centred" come from the family's own variables - the text's
 * alignment and whether the column is centred in the page - because a centred
 * heading in a left-aligned column is a heading floating off to one side.
 */
const HEADING_BLOCK: CSSProperties = {
  textAlign: 'var(--storefront-heading-align, left)' as CSSProperties['textAlign'],
  marginInline: 'var(--storefront-heading-inset, 0)',
};

function SectionHeading({
  section,
  title,
  subtitle,
  className = 'mb-8',
  align = 'theme',
}: {
  /** The section the words belong to, so they can be typed into on the page. */
  section: Section;
  title?: string;
  subtitle?: string;
  /** The space under it; a rail sets its own. */
  className?: string;
  /** The theme's own alignment, or the section's choice over it. */
  align?: 'theme' | 'left' | 'center';
}) {
  if (!title && !subtitle) return null;

  const placed: CSSProperties =
    align === 'theme'
      ? HEADING_BLOCK
      : { textAlign: align, marginInline: align === 'center' ? 'auto' : 0 };
  return (
    <div className={cn(align === 'center' ? 'max-w-3xl' : 'max-w-2xl', className)} style={placed}>
      {title ? (
        <h2 className="text-2xl tracking-tight sm:text-3xl" style={STOREFRONT_HEADING_STYLE}>
          <Editable section={section} field="title" value={title} emphasis>
            <HeadingText>{title}</HeadingText>
          </Editable>
        </h2>
      ) : null}
      {subtitle ? (
        <p className="mt-2 text-base" style={{ color: 'var(--storefront-muted)' }}>
          <Editable section={section} field="subtitle" value={subtitle}>
            {subtitle}
          </Editable>
        </p>
      ) : null}
    </div>
  );
}

function CtaButton({
  label,
  href,
  section,
  blockId,
  flush = false,
  variant = 'button',
  onImage = false,
}: {
  label: string;
  href: string;
  /** Where the label is written back when it is edited on the page. */
  section: Section;
  blockId?: string;
  /** No space above it: it stands in a row that keeps its own. */
  flush?: boolean;
  /** The theme's button, or an underlined text link in the primary colour. */
  variant?: 'button' | 'link';
  /** Over a photograph, where the link goes white to stay readable. */
  onImage?: boolean;
}) {
  if (!label) return null;

  return (
    <SectionLink
      href={href}
      className={
        variant === 'link'
          ? cn(
              'inline-block text-sm font-bold uppercase tracking-wide underline decoration-2 underline-offset-[6px] hover:opacity-80',
              !flush && 'mt-8',
            )
          : cn('inline-flex h-12 items-center px-7 text-sm font-semibold', !flush && 'mt-7')
      }
      style={
        variant === 'link'
          ? {
              color: onImage ? '#ffffff' : 'var(--storefront-text)',
              textDecorationColor: onImage ? '#ffffff' : 'var(--storefront-primary)',
            }
          : STOREFRONT_BUTTON_STYLE
      }
    >
      {/* Isolated: a click to place the caret in "Shop now" is not a click on
          the button, and must not follow its link. */}
      <Editable section={section} field="cta_label" blockId={blockId} value={label} isolateClick>
        {label}
      </Editable>
    </SectionLink>
  );
}

/**
 * One block, wrapped by the customiser and unwrapped everywhere else.
 *
 * The wrapper is what makes a slide, a promise or a review clickable in the
 * preview and outlined while it is selected. On the shopper's storefront no
 * wrapper exists at all: `blockWrap` is unset, the fragment renders the block's
 * own markup straight into its grid, and the page is the page it always was.
 */
function BlockFrame({
  section,
  block,
  children,
}: {
  section: Section;
  block: Block;
  children: ReactNode;
}) {
  const { blockWrap, includeHidden } = useEnvironment();
  const body =
    includeHidden && !block.visible ? (
      <div className="opacity-40">{children}</div>
    ) : (
      children
    );
  // A stand-in block is not in the merchant's document, so there is no row in
  // the tree for it and no settings behind it. Wrapping it would offer a
  // selection that leads nowhere; it renders bare instead, and clicking it
  // selects the section it is standing in for.
  const wrappable = blockWrap && !isPlaceholderId(block.id);
  return <>{wrappable ? blockWrap(section, block, body) : body}</>;
}

/** Where a section with no content yet says so - in the preview only. */
function PreviewHint({ children }: { children: ReactNode }) {
  const { preview } = useEnvironment();
  if (!preview) return null;

  return (
    <p
      className="rounded-xl border border-dashed px-4 py-6 text-center text-sm"
      style={{ borderColor: 'var(--storefront-muted)', color: 'var(--storefront-muted)' }}
    >
      {children}
    </p>
  );
}

// --- Sections -------------------------------------------------------------
/**
 * How tall a banner stands, as a floor rather than a fixed height: a long
 * heading grows the banner instead of spilling out of it.
 *
 * The viewport units only take over from `sm:` up. Four-fifths of a phone
 * screen spent on one photograph is a shop the shopper has to scroll past
 * before they see a single product, so small screens keep fixed heights.
 */
const BANNER_HEIGHT: Record<BannerHeight, string> = {
  sm: 'min-h-72 sm:min-h-[45vh]',
  md: 'min-h-96 sm:min-h-[62vh]',
  lg: 'min-h-[30rem] sm:min-h-[78vh]',
  full: 'min-h-[36rem] sm:min-h-[calc(100svh_-_var(--storefront-header-height,4rem))]',
};

/**
 * The picture beside a split hero, which is an illustration rather than a
 * backdrop - it stays a photograph next to the copy at every height.
 */
const SPLIT_IMAGE_HEIGHT: Record<BannerHeight, string> = {
  sm: 'h-64',
  md: 'h-80',
  lg: 'h-[26rem]',
  full: 'h-[32rem]',
};

/**
 * A split hero's overlapping product, by `product_image_size`: the picture's
 * box, and how much room the words keep free for the half standing over them.
 */
/**
 * A flush split hero's height. Taller than a framed picture's, which sits in
 * padding: here the photograph *is* half the card, edge to edge.
 */
const FLUSH_IMAGE_HEIGHT: Record<BannerHeight, string> = {
  sm: 'h-72 lg:h-auto lg:min-h-[24rem]',
  md: 'h-80 lg:h-auto lg:min-h-[30rem]',
  lg: 'h-96 lg:h-auto lg:min-h-[36rem]',
  full: 'h-96 lg:h-auto lg:min-h-[42rem]',
};

/**
 * The product over a flush hero's seam: a size up, as the card is taller -
 * and the room the words leave for the half of it that stands over them.
 */
const FLUSH_PRODUCT: Record<HeroSection['product_image_size'], { box: string; copy: string }> = {
  sm: { box: 'w-24 lg:w-52', copy: 'lg:pe-32' },
  md: { box: 'w-28 lg:w-64', copy: 'lg:pe-36' },
  lg: { box: 'w-32 lg:w-80', copy: 'lg:pe-44' },
};

const HERO_PRODUCT_SIZE: Record<HeroSection['product_image_size'], { box: string; copy: string }> = {
  sm: { box: 'w-24 lg:w-40', copy: 'lg:pr-16' },
  md: { box: 'w-28 lg:w-52', copy: 'lg:pr-24' },
  lg: { box: 'w-32 lg:w-64', copy: 'lg:pr-28' },
};

/**
 * Whether a banner runs edge to edge, outside the page column.
 *
 * Only image-backed banners bleed. A text-only hero pinned to the window edges
 * is a heading and a paragraph stretched across a whole desktop monitor, which
 * reads worse than the framed version, not better.
 */
function heroBleeds(section: HeroSection): boolean {
  return section.width === 'full' && section.layout === 'center' && heroImages(section).length > 0;
}

function slideshowBleeds(section: SlideshowSection): boolean {
  return section.width === 'full' && section.blocks.length > 0;
}

/**
 * What a contained split hero paints as its card, or null when it is not one.
 *
 * Contained is what the setting says: the banner keeps to the page column.
 * A centred hero has always honoured it; a split one used to paint its
 * colour across the whole window regardless. Now its colour is a rounded card
 * in the column, the same edges every other section stands between - and the
 * band around it is the page's own. Nothing to contain without a colour.
 */
function heroCardFill(section: Section): string | null {
  if (section.type !== 'hero' || section.layout !== 'split' || section.width !== 'contained') return null;
  return resolveColor(section.background_color) ?? BACKGROUNDS[section.background] ?? null;
}

/**
 * What a slider showcase paints as its panel, or null when it is not one: its
 * heading stands on the page above, so the band's colour is the panel's.
 */
function showcasePanelFill(section: Section): string | null {
  if (section.type !== 'product_showcase' || section.layout !== 'carousel') return null;
  return resolveColor(section.background_color) ?? BACKGROUNDS[section.background] ?? null;
}

/** Whether a section paints its colour as a card of its own rather than as its band. */
function paintsOwnCard(section: Section): boolean {
  return Boolean(heroCardFill(section) ?? showcasePanelFill(section));
}

function sectionBleeds(section: Section): boolean {
  if (section.type === 'hero') return heroBleeds(section);
  if (section.type === 'slideshow') return slideshowBleeds(section);
  return false;
}

/**
 * A hero's buttons and the benefit badges under them.
 *
 * The first button is every section's button. The second is outlined, so the
 * two never compete: "Book an appointment" stays the thing to do, "Call us"
 * the alternative. Over a photograph it is drawn in white, as the copy around
 * it is - the theme's primary colour would sink into a dark picture. The
 * badges are short claims with a tick, read at a glance under the buttons.
 *
 * Nothing is drawn for a hero with none of the three - every hero saved before
 * the second button and badges existed has at most its one button, and keeps
 * exactly the markup it always had.
 */
function HeroActions({
  section,
  onImage,
  centered,
}: {
  section: HeroSection;
  onImage: boolean;
  centered: boolean;
}) {
  const primary = section.cta_label;
  const secondary = section.secondary_cta_label;
  // Kept with their place in the list: a badge typed into on the page is
  // written back by position, and a blank one left in the editor is skipped.
  const badges = section.badges
    .map((badge, index) => ({ badge, index }))
    .filter(({ badge }) => badge.trim());
  if (!primary && !secondary && badges.length === 0) return null;

  const outline: CSSProperties = onImage
    ? { color: '#ffffff', border: '1px solid #ffffff', borderRadius: 'var(--storefront-radius)' }
    : {
        color: 'var(--storefront-primary)',
        border: '1px solid var(--storefront-primary)',
        borderRadius: 'var(--storefront-radius)',
      };

  return (
    <>
      {primary || secondary ? (
        <div
          data-testid="hero-buttons"
          className={cn('mt-7 flex flex-wrap items-center gap-3', centered && 'justify-center')}
        >
          <CtaButton
            label={primary}
            href={section.cta_href}
            section={section}
            variant={section.cta_style}
            onImage={onImage}
            flush
          />
          {secondary ? (
            <SectionLink
              href={section.secondary_cta_href}
              className="inline-flex h-12 items-center bg-transparent px-7 text-sm font-semibold"
              style={outline}
            >
              <Editable section={section} field="secondary_cta_label" value={secondary} isolateClick>
                {secondary}
              </Editable>
            </SectionLink>
          ) : null}
        </div>
      ) : null}
      {badges.length > 0 ? (
        <ul
          data-testid="hero-badges"
          className={cn(
            'mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm font-medium',
            centered && 'justify-center',
          )}
        >
          {badges.map(({ badge, index }) => (
            <li key={index} className="inline-flex items-center gap-1.5">
              <Check
                className="size-4 shrink-0"
                style={{ color: onImage ? '#ffffff' : 'var(--storefront-primary)' }}
                aria-hidden
              />
              <Editable section={section} field={`badges.${index}`} value={badge}>
                {badge}
              </Editable>
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

function Hero({ section }: { section: HeroSection }) {
  const { storeName, preview } = useEnvironment();
  // `text`, not `copy`: this component already calls its heading block that.
  const text = useShopContent();
  // The builder's prompt is for the merchant: on the live shop a hero still
  // carrying it speaks as the shop's welcome instead, with no tagline.
  const unwritten = !preview && isPlaceholderHero(section);
  const heading =
    (unwritten ? '' : section.heading) || text('section.hero_welcome', { store: storeName });
  const subheading = unwritten ? '' : section.subheading;
  const items = heroImageItems(section);
  const images = items.map((each) => each.image_url);
  const [current, setCurrent] = useState(0);

  // Paused in the customizer preview, like the slideshow: a banner cycling
  // under the merchant while they swap the second picture is unusable.
  const rotating = section.image_autoplay && !preview && images.length > 1;

  useEffect(() => {
    if (!rotating) return;
    const timer = window.setInterval(
      () => setCurrent((index) => (index + 1) % images.length),
      section.image_interval_seconds * 1000,
    );
    return () => window.clearInterval(timer);
  }, [rotating, images.length, section.image_interval_seconds]);
  usePreloadNext(
    images,
    current,
    section.layout === 'split' ? '(min-width: 1024px) 50vw, 100vw' : '100vw',
    rotating,
  );

  // The list can shrink under the index while a merchant deletes pictures.
  const shown = items.length > 0 ? items[Math.min(current, items.length - 1)] : null;
  const image = shown?.image_url ?? '';
  const focal = focalStyle(shown?.image_focal);
  // Only the centred layout puts the copy on top of the photograph. There, the
  // supporting line cannot keep the theme's muted grey - it would disappear
  // into a dark image - so it goes white and leans on opacity instead.
  const onImage = section.layout === 'center' && Boolean(image);

  // The way through by hand, and the only way through when the merchant turned
  // rotation off. Sits under the copy in the centred layout and under the
  // photograph in the split one, so it never lands on the heading.
  const dots =
    images.length > 1 ? (
      <div className="flex justify-center gap-2">
        {images.map((each, index) => (
          <button
            key={`${each}:${index}`}
            type="button"
            aria-label={text('section.show_image', { number: index + 1 })}
            aria-current={index === current}
            onClick={() => setCurrent(index)}
            className={cn(
              'size-2 rounded-full border transition-opacity',
              index === current ? 'opacity-100' : 'opacity-50 hover:opacity-80',
            )}
            style={{
              backgroundColor: onImage ? '#ffffff' : 'var(--storefront-primary)',
              borderColor: onImage ? '#ffffff' : 'var(--storefront-primary)',
            }}
          />
        ))}
      </div>
    ) : null;

  const copy = (
    <>
      {section.eyebrow ? (
        <p
          className="mb-4 text-sm font-semibold uppercase tracking-[0.08em]"
          style={{ color: onImage ? '#ffffff' : 'var(--storefront-primary)' }}
        >
          <Editable section={section} field="eyebrow" value={section.eyebrow}>
            {section.eyebrow}
          </Editable>
        </p>
      ) : null}
      <h1
        className="text-3xl tracking-tight sm:text-5xl"
        style={{ ...STOREFRONT_HEADING_STYLE, lineHeight: 1.1 }}
      >
        {/* Edited as what is shown: a hero with no heading of its own shows
            the shop's welcome, and typing over that is how one is given. */}
        <Editable section={section} field="heading" value={heading} emphasis>
          <HeadingText>{heading}</HeadingText>
        </Editable>
      </h1>
      {subheading ? (
        <p
          className={cn('mt-4 max-w-xl text-lg', onImage && 'opacity-90')}
          style={onImage ? undefined : { color: 'var(--storefront-muted)' }}
        >
          <Editable section={section} field="subheading" value={subheading}>
            {subheading}
          </Editable>
        </p>
      ) : null}
      <HeroActions section={section} onImage={onImage} centered={section.layout === 'center'} />
    </>
  );

  if (section.layout === 'split') {
    const product = section.product_image_url;
    const size = HERO_PRODUCT_SIZE[section.product_image_size] ?? HERO_PRODUCT_SIZE.md;
    const card = heroCardFill(section);
    const grid = 'grid items-center gap-10 lg:grid-cols-2';

    // Flush: the photograph fills its half of the card to the card's own
    // edges - no inner frame - and the product stands over the seam, centred
    // up and down. Only a card has edges to fill; elsewhere it stays framed.
    if (card && image && section.image_fit === 'flush') {
      const flush = FLUSH_PRODUCT[section.product_image_size] ?? FLUSH_PRODUCT.md;
      return (
        <Container>
          <div
            data-testid="hero-card"
            data-image-fit="flush"
            className="grid overflow-hidden lg:grid-cols-2"
            style={{ backgroundColor: card, borderRadius: 'var(--storefront-radius)' }}
          >
            <div className={cn('self-center px-6 py-10 sm:px-10 lg:py-16 lg:ps-14', product ? flush.copy : 'lg:pe-14')}>
              {copy}
            </div>
            <div className={cn('relative', FLUSH_IMAGE_HEIGHT[section.height])}>
              <StorefrontImage
                url={image}
                sizes="(min-width: 1024px) 50vw, 100vw"
                alt=""
                className="absolute inset-0 size-full object-cover"
                style={focal}
              />
              {product ? (
                <div
                  data-testid="hero-product"
                  className={cn(
                    'pointer-events-none absolute bottom-4 left-1/2 z-10 aspect-square max-w-[45%] -translate-x-1/2',
                    'lg:top-1/2 lg:bottom-auto lg:left-0 lg:max-w-none lg:-translate-x-1/2 lg:-translate-y-1/2',
                    flush.box,
                  )}
                >
                  <StorefrontImage
                    url={product}
                    sizes="(min-width: 1024px) 18rem, 8rem"
                    alt=""
                    placeholder={false}
                    className="size-full object-contain drop-shadow-2xl"
                  />
                </div>
              ) : null}
              {dots ? <div className="absolute inset-x-0 bottom-4 z-20">{dots}</div> : null}
            </div>
          </div>
        </Container>
      );
    }

    const columns = (
      <>
        {/* Room on the right for the half of the product that stands over the words. */}
        <div className={cn(product && image && size.copy)}>{copy}</div>
        {image ? (
          <div className="space-y-3">
            <div className={cn('relative', product && 'pb-10 lg:pb-0')}>
              {/* The frame clips, so a zoomed picture stays inside its corners. */}
              <div className="overflow-hidden" style={{ borderRadius: 'var(--storefront-radius)' }}>
                <StorefrontImage
                  url={image}
                  sizes="(min-width: 1024px) 50vw, 100vw"
                  alt=""
                  className={cn('w-full object-cover', SPLIT_IMAGE_HEIGHT[section.height])}
                  style={focal}
                />
              </div>
              {product ? (
                // Centred over the seam between the columns on a wide screen
                // (the gap is 2.5rem, so the seam is 1.25rem left of the photo);
                // on a phone smaller, centred over the photo's bottom edge.
                // Never wider than the column, so it cannot push the page sideways.
                <div
                  data-testid="hero-product"
                  className={cn(
                    'pointer-events-none absolute bottom-0 left-1/2 z-10 aspect-square max-w-[45%] -translate-x-1/2',
                    'lg:bottom-[8%] lg:left-[-1.25rem] lg:max-w-none',
                    size.box,
                  )}
                >
                  <StorefrontImage
                    url={product}
                    sizes="(min-width: 1024px) 18rem, 8rem"
                    alt=""
                    placeholder={false}
                    className="size-full object-contain drop-shadow-2xl"
                  />
                </div>
              ) : null}
            </div>
            {dots}
          </div>
        ) : product ? (
          // No photo yet: the product stands on its own in the picture column.
          <div data-testid="hero-product" className={cn('mx-auto aspect-square', size.box)}>
            <StorefrontImage
              url={product}
              sizes="(min-width: 1024px) 18rem, 8rem"
              alt=""
              placeholder={false}
              className="size-full object-contain drop-shadow-2xl"
            />
          </div>
        ) : null}
      </>
    );
    return card ? (
      <Container>
        <div
          data-testid="hero-card"
          className={cn(grid, 'px-6 py-10 sm:px-10 lg:px-14 lg:py-14')}
          style={{ backgroundColor: card, borderRadius: 'var(--storefront-radius)' }}
        >
          {columns}
        </div>
      </Container>
    ) : (
      <Container className={grid}>{columns}</Container>
    );
  }

  // Centred: the image is the backdrop, dimmed by however much the merchant
  // asked for so the heading stays readable over a busy photograph. Full width
  // it fills the window edge to edge, with no radius to give its corners away;
  // contained it sits in the page column as a rounded card.
  const bleed = heroBleeds(section);
  const banner = (
    <div
      className={cn(
        'relative grid place-items-center overflow-hidden px-6 py-14 text-center',
        image && BANNER_HEIGHT[section.height],
      )}
      style={{
        borderRadius: image && !bleed ? 'var(--storefront-radius)' : undefined,
        // Full width only: a theme may cut the bottom edge on a diagonal
        // (`heroEdge`). `none` for every other theme, and never on a card,
        // whose rounded corners are already its edge.
        clipPath: image && bleed ? 'var(--storefront-hero-clip, none)' : undefined,
      }}
    >
      {image ? (
        <>
          <StorefrontImage
            url={image}
            sizes="100vw"
            alt=""
            className="absolute inset-0 size-full object-cover"
            style={focal}
          />
          <span
            aria-hidden
            className="absolute inset-0 bg-black"
            style={{ opacity: section.overlay_opacity / 100 }}
          />
        </>
      ) : null}
      <div
        className="relative mx-auto flex max-w-2xl flex-col items-center"
        style={image ? { color: '#ffffff' } : undefined}
      >
        {copy}
        {dots ? <div className="mt-8">{dots}</div> : null}
      </div>
    </div>
  );

  return bleed ? banner : <Container>{banner}</Container>;
}

function Slideshow({ section }: { section: SlideshowSection }) {
  const { preview, includeHidden } = useEnvironment();
  const copy = useShopContent();
  const slides = shownBlocks(section.blocks, includeHidden);
  const [current, setCurrent] = useState(0);

  // Autoplay is paused in the preview: a banner cycling under the merchant
  // while they edit slide two is unusable.
  const rotating = section.autoplay && !preview && slides.length > 1;

  useEffect(() => {
    if (!rotating) return;
    const timer = window.setInterval(
      () => setCurrent((index) => (index + 1) % slides.length),
      section.interval_seconds * 1000,
    );
    return () => window.clearInterval(timer);
  }, [rotating, slides.length, section.interval_seconds]);
  usePreloadNext(
    slides.map((each) => each.image_url ?? ''),
    current,
    '100vw',
    rotating,
  );

  if (slides.length === 0) {
    return (
      <Container>
        <PreviewHint>Add a slide to see this slideshow.</PreviewHint>
      </Container>
    );
  }

  const slide = slides[Math.min(current, slides.length - 1)];
  const bleed = slideshowBleeds(section);

  const banner = (
    // Framed as the slide rather than as the slideshow: the merchant clicking
    // a banner in the preview means the picture and words in front of them,
    // which is one slide of several.
    <BlockFrame section={section} block={slide}>
    <div
      className={cn(
        'relative grid place-items-center overflow-hidden px-6 py-14 text-center',
        BANNER_HEIGHT[section.height],
      )}
      style={{
        borderRadius: bleed ? undefined : 'var(--storefront-radius)',
        backgroundColor: 'var(--storefront-surface)',
      }}
    >
      {slide.image_url ? (
        <>
          <StorefrontImage
            url={slide.image_url}
            sizes="100vw"
            alt=""
            className="absolute inset-0 size-full object-cover"
            style={focalStyle(slide.image_focal)}
          />
          <span aria-hidden className="absolute inset-0 bg-black/35" />
        </>
      ) : null}
      <div
        className="relative mx-auto max-w-2xl"
        style={slide.image_url ? { color: '#ffffff' } : undefined}
      >
        {slide.heading ? (
          <h2 className="text-3xl tracking-tight sm:text-4xl" style={STOREFRONT_HEADING_STYLE}>
            <Editable section={section} field="heading" blockId={slide.id} value={slide.heading}>
              {slide.heading}
            </Editable>
          </h2>
        ) : null}
        {slide.subheading ? (
          <p className="mt-3 text-lg">
            <Editable section={section} field="subheading" blockId={slide.id} value={slide.subheading}>
              {slide.subheading}
            </Editable>
          </p>
        ) : null}
        <CtaButton label={slide.cta_label} href={slide.cta_href} section={section} blockId={slide.id} />
      </div>

      {slides.length > 1 ? (
        <div className="absolute bottom-4 flex gap-2">
          {slides.map((each, index) => (
            <button
              key={each.id}
              type="button"
              aria-label={copy('section.show_slide', { number: index + 1 })}
              aria-current={index === current}
              onClick={() => setCurrent(index)}
              className={cn(
                'size-2.5 rounded-full transition-opacity',
                index === current ? 'opacity-100' : 'opacity-40',
              )}
              style={{ backgroundColor: slide.image_url ? '#ffffff' : 'var(--storefront-primary)' }}
            />
          ))}
        </div>
      ) : null}
    </div>
    </BlockFrame>
  );

  return bleed ? banner : <Container>{banner}</Container>;
}

/** The shared card body, wrapped in this renderer's preview-aware link. */
function ProductCardLink({
  product,
  columns,
  presentation,
}: {
  product: StorefrontProductSummary;
  // Passed down rather than read from a context: the card's slot is as wide as
  // the grid divides the page into, and only the section knows that number.
  columns: number;
  presentation?: CardPresentation;
}) {
  const { productHref } = useEnvironment();

  return (
    <SectionLink
      href={productHref(product.slug)}
      className={columns > 5 ? cn('group block h-full', DENSE_CARD) : 'group block h-full'}
    >
      <ProductCard product={product} presentation={presentation} sizes={gridImageSizes(columns)} />
    </SectionLink>
  );
}

/**
 * How many slider cards are in view at once, by breakpoint: the section's
 * per-row number on a wide screen, stepping down to two on a phone. The gap
 * widens with the screen, as the grid's does.
 */
const CAROUSEL_ACROSS: Record<number, string> = {
  2: '[--rail-across:2] [--rail-gap:1rem] lg:[--rail-gap:2rem]',
  3: '[--rail-across:2] md:[--rail-across:3] [--rail-gap:1rem] lg:[--rail-gap:2rem]',
  4: '[--rail-across:2] md:[--rail-across:3] lg:[--rail-across:4] [--rail-gap:1rem] lg:[--rail-gap:2rem]',
  5: '[--rail-across:2] sm:[--rail-across:3] md:[--rail-across:4] lg:[--rail-across:5] [--rail-gap:1rem] lg:[--rail-gap:1.5rem]',
  // Dense marketplace rows: the full count only on a wide desktop.
  6: '[--rail-across:2] sm:[--rail-across:3] md:[--rail-across:4] lg:[--rail-across:6] [--rail-gap:0.75rem] lg:[--rail-gap:1rem]',
  7: '[--rail-across:2] sm:[--rail-across:3] md:[--rail-across:4] lg:[--rail-across:5] xl:[--rail-across:7] [--rail-gap:0.75rem] lg:[--rail-gap:1rem]',
  8: '[--rail-across:2] sm:[--rail-across:3] md:[--rail-across:4] lg:[--rail-across:6] xl:[--rail-across:8] [--rail-gap:0.75rem] lg:[--rail-gap:1rem]',
  9: '[--rail-across:3] md:[--rail-across:5] lg:[--rail-across:7] xl:[--rail-across:9] [--rail-gap:0.75rem] lg:[--rail-gap:1rem]',
  10: '[--rail-across:3] md:[--rail-across:5] lg:[--rail-across:8] xl:[--rail-across:10] [--rail-gap:0.75rem] lg:[--rail-gap:1rem]',
};

/**
 * Past five across a product card has no room for a long name or a large
 * type size: the name shrinks and stops at two lines. Two to five keep the
 * card exactly as it was.
 */
const DENSE_CARD = 'min-w-0 [&_h3]:line-clamp-2 [&_h3]:break-words [&_h3]:text-sm';

function FeaturedProducts({ section }: { section: FeaturedProductsSection }) {
  const { productsFor, loading } = useEnvironment();
  const copy = useShopContent();
  const shown = productsFor(section.category_id).slice(0, section.limit);

  if (section.layout === 'carousel' && shown.length > 0) {
    return (
      <Container>
        <Rail
          label={section.title}
          itemCount={shown.length}
          across={CAROUSEL_ACROSS[section.columns] ?? CAROUSEL_ACROSS[4]}
          arrows="bare"
          heading={
            <SectionHeading
              section={section}
              title={section.title}
              subtitle={section.subtitle}
              className="mb-4"
            />
          }
        >
          {shown.map((product) => (
            <li key={product.id} className={RAIL_ITEM}>
              <ProductCardLink product={product} columns={section.columns} presentation="panel" />
            </li>
          ))}
        </Rail>
      </Container>
    );
  }

  return (
    <Container>
      <SectionHeading section={section} title={section.title} subtitle={section.subtitle} />
      {shown.length === 0 ? (
        loading ? (
          <div className={cn('grid gap-6', columnClass(section.columns))}>
            {Array.from({ length: section.columns }, (_, index) => (
              <div
                key={index}
                className="aspect-square w-full animate-pulse"
                style={{
                  backgroundColor: 'var(--storefront-surface)',
                  borderRadius: 'var(--storefront-radius)',
                }}
              />
            ))}
          </div>
        ) : (
          <p className="text-sm" style={{ color: 'var(--storefront-muted)' }}>
            {section.category_id ? copy('listing.empty_category') : copy('listing.empty')}
          </p>
        )
      ) : (
        <div className={cn('grid gap-6', columnClass(section.columns))}>
          {shown.map((product) => (
            <ProductCardLink key={product.id} product={product} columns={section.columns} />
          ))}
        </div>
      )}
    </Container>
  );
}

/** Four across: the section has no per-row setting of its own. */
const RECENTLY_VIEWED_COLUMNS = 4;

/**
 * The products this shopper opened lately, from their own browser's trail,
 * drawn with the product grid's own cards. Off the page (the band too) when
 * the trail is empty or holds only the product on screen. The customiser,
 * which has no shopper, draws stand-ins so the merchant can see it.
 */
function RecentlyViewed({ section }: { section: RecentlyViewedSection }) {
  const { product, placeholders, preview } = useEnvironment();
  // The customiser's trail would be the merchant's own browsing; never shown.
  const live = !preview && !placeholders;
  const { products } = useRecentlyViewedProducts(section.limit, product?.slug, live);
  const shown = placeholders ? PLACEHOLDER_PRODUCTS.slice(0, section.limit) : (products ?? []);

  if (shown.length === 0) return <div data-section-empty hidden />;

  if (section.layout === 'carousel') {
    return (
      <Container>
        <Rail
          label={section.title}
          itemCount={shown.length}
          across={CAROUSEL_ACROSS[RECENTLY_VIEWED_COLUMNS]}
          arrows="bare"
          heading={<SectionHeading section={section} title={section.title} className="mb-4" />}
        >
          {shown.map((item) => (
            <li key={item.id} className={RAIL_ITEM}>
              <ProductCardLink product={item} columns={RECENTLY_VIEWED_COLUMNS} presentation="panel" />
            </li>
          ))}
        </Rail>
      </Container>
    );
  }

  return (
    <Container>
      <SectionHeading section={section} title={section.title} />
      <div className={cn('grid gap-6', columnClass(RECENTLY_VIEWED_COLUMNS))}>
        {shown.map((item) => (
          <ProductCardLink key={item.id} product={item} columns={RECENTLY_VIEWED_COLUMNS} />
        ))}
      </div>
    </Container>
  );
}

/**
 * "New arrivals | Best sellers | On sale" over one product row, drawn with the
 * product grid's own cards, grid and slider. Which tabs show and what is under
 * each is `useProductTabs`; a section whose every tab came back empty leaves a
 * marker that takes its whole band off the page (see `SectionView`).
 */
function ProductTabs({ section }: { section: ProductTabsSection }) {
  const { productQuery, productsFor, preview, placeholders, includeHidden, categories } =
    useEnvironment();
  const copy = useShopContent();
  const circles = section.tab_style === 'circles';
  // A round tab's own picture: the merchant's, else its category's.
  const ownPicture = (tab: ProductTabBlock): string | null =>
    tab.image_url ||
    (tab.source === 'category' && tab.category_id
      ? categories.find((category) => category.id === tab.category_id)?.image_url
      : null) ||
    null;
  const state = useProductTabs(section, {
    productQuery,
    productsFor,
    keepEmpty: preview,
    includeHidden,
    fallback: placeholders ? PLACEHOLDER_PRODUCTS : [],
    // A round tab with no picture of its own shows its first product's, so
    // its products are asked for up front rather than on first open.
    eager: circles ? (tab) => !ownPicture(tab) : undefined,
  });

  if (state.empty) return <div data-section-empty hidden />;

  const label = section.title || state.active?.label || '';
  const shown = state.products.slice(0, section.limit);
  // A single round category is still a picture worth showing; a single
  // underlined word over its own row is not.
  const tabs =
    state.tabs.length > 1 || preview || (circles && state.tabs.length > 0) ? (
      <ProductTabList
        section={section}
        tabs={state.tabs}
        active={state.active}
        onSelect={state.select}
        label={label}
        pictureFor={(tab) => ownPicture(tab) ?? state.pictures[tab.id] ?? null}
        wrapTab={(tab, node) => (
          <BlockFrame section={section} block={tab}>
            {node}
          </BlockFrame>
        )}
      />
    ) : null;
  const panel = {
    id: productTabPanelId(section.id),
    role: tabs ? 'tabpanel' : undefined,
    'aria-labelledby': state.active && tabs ? productTabId(section.id, state.active.id) : undefined,
    'aria-busy': state.loading || undefined,
  };

  return (
    <Container>
      <SectionHeading section={section} title={section.title} subtitle={section.subtitle} />
      {tabs}
      <div {...panel}>
        {state.loading ? (
          <div className={cn('grid gap-6', columnClass(section.columns))}>
            {Array.from({ length: section.columns }, (_, index) => (
              <div
                key={index}
                className="aspect-square w-full animate-pulse"
                style={{
                  backgroundColor: 'var(--storefront-surface)',
                  borderRadius: 'var(--storefront-radius)',
                }}
              />
            ))}
          </div>
        ) : shown.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--storefront-muted)' }}>
            {copy('listing.empty')}
          </p>
        ) : section.layout === 'carousel' ? (
          <Rail
            label={label}
            itemCount={shown.length}
            across={CAROUSEL_ACROSS[section.columns] ?? CAROUSEL_ACROSS[4]}
            arrows="bare"
            heading={null}
          >
            {shown.map((product) => (
              <li key={product.id} className={RAIL_ITEM}>
                <ProductCardLink product={product} columns={section.columns} presentation="panel" />
              </li>
            ))}
          </Rail>
        ) : (
          <div className={cn('grid gap-6', columnClass(section.columns))}>
            {shown.map((product) => (
              <ProductCardLink key={product.id} product={product} columns={section.columns} />
            ))}
          </div>
        )}
      </div>
      {section.cta_label ? (
        <div className="text-center">
          <CtaButton
            label={section.cta_label}
            href={section.cta_href || '/products'}
            section={section}
          />
        </div>
      ) : null}
    </Container>
  );
}

/**
 * A department tile standing on a light panel: the picture centred over the
 * panel and overhanging its bottom edge, the name centred under it. Made for
 * cut-out product shots; a photograph gets rounded corners of its own. The
 * panel and the name take the theme's primary while the tile is pointed at.
 */
function StandTile({ category, columns }: { category: StorefrontCategory; columns: number }) {
  return (
    <SectionLink href={`/products?category=${category.slug}`} className="group block text-center">
      <div className="relative pt-8">
        {/* Classes rather than an inline colour, so hovering - or tabbing to
            - the tile can fill the panel with the theme's primary. */}
        <div
          aria-hidden
          data-stand-panel=""
          className={cn(
            'absolute inset-x-0 top-0 h-[78%] transition-colors duration-300',
            'bg-[color-mix(in_srgb,var(--storefront-background)_15%,#ffffff)]',
            'group-hover:bg-[var(--storefront-primary)] group-focus-visible:bg-[var(--storefront-primary)]',
          )}
          style={{ borderRadius: 'var(--storefront-radius)' }}
        />
        <div className="relative mx-auto aspect-square w-[72%] transition-transform duration-300 group-hover:-translate-y-1.5">
          {category.image_url ? (
            <StorefrontImage
              url={category.image_url}
              sizes={gridImageSizes(columns)}
              alt={category.name}
              className="size-full object-contain drop-shadow-xl"
              style={{ borderRadius: 'calc(var(--storefront-radius) / 2)' }}
            />
          ) : null}
        </div>
      </div>
      <p
        className={cn(
          'mt-5 text-lg transition-colors group-hover:text-[var(--storefront-primary)] group-focus-visible:text-[var(--storefront-primary)]',
          // A dense row of small tiles: a smaller name that stops at two lines.
          columns > 5 && 'mt-3 line-clamp-2 break-words text-sm',
        )}
      >
        {category.name}
      </p>
    </SectionLink>
  );
}

/** A department tile filled with its picture, the name under it. */
function PhotoTile({ category, columns }: { category: StorefrontCategory; columns: number }) {
  return (
    <SectionLink href={`/products?category=${category.slug}`} className="group block">
      <div
        className="aspect-[4/3] w-full overflow-hidden"
        style={{
          backgroundColor: 'var(--storefront-surface)',
          borderRadius: 'var(--storefront-radius)',
        }}
      >
        {category.image_url ? (
          <StorefrontImage
            url={category.image_url}
            sizes={gridImageSizes(columns)}
            alt={category.name}
            className={cn('size-full', STOREFRONT_TILE_IMAGE, 'transition-transform group-hover:scale-105')}
          />
        ) : null}
      </div>
      <p
        className={
          columns > 5
            ? 'mt-2 line-clamp-2 break-words text-xs font-semibold sm:text-sm'
            : 'mt-3 text-sm font-semibold'
        }
      >
        {category.name}
      </p>
    </SectionLink>
  );
}

function Categories({ section }: { section: CategoriesSection }) {
  const { categories } = useEnvironment();
  const copy = useShopContent();
  // Only top-level categories: a home page is a way in, not the whole tree.
  const shown = categories.filter((category) => !category.parent_id);
  const Tile = section.tile_style === 'stand' ? StandTile : PhotoTile;

  return (
    <Container>
      <SectionHeading
        section={section}
        title={section.title}
        subtitle={section.subtitle}
        align={section.heading_align}
      />
      {shown.length === 0 ? (
        <p className="text-sm" style={{ color: 'var(--storefront-muted)' }}>
          {copy('section.categories_empty')}
        </p>
      ) : section.layout === 'carousel' ? (
        <Rail
          label={section.title}
          itemCount={shown.length}
          across={CAROUSEL_ACROSS[section.columns] ?? CAROUSEL_ACROSS[4]}
          heading={null}
          progress
        >
          {shown.map((category) => (
            <li key={category.id} className={RAIL_ITEM}>
              <Tile category={category} columns={section.columns} />
            </li>
          ))}
        </Rail>
      ) : (
        <div className={cn('grid gap-6', columnClass(section.columns))}>
          {shown.map((category) => (
            <Tile key={category.id} category={category} columns={section.columns} />
          ))}
        </div>
      )}
    </Container>
  );
}

/** A thin row of the shop's own department links - see `CategoryStripSection.tsx`. */
function CategoryStrip({ section }: { section: CategoryStripSection }) {
  const { categories } = useEnvironment();
  const copy = useShopContent();
  return (
    <CategoryStripView
      section={section}
      categories={categories}
      heading={<SectionHeading section={section} title={section.title} className="mb-3" />}
      emptyText={copy('section.categories_empty')}
      link={(href, className, children) => (
        <SectionLink href={href} className={className}>
          {children}
        </SectionLink>
      )}
    />
  );
}

function ImageWithText({ section }: { section: ImageWithTextSection }) {
  const { preview, includeHidden } = useEnvironment();
  // With no picture, a shopper would see an empty grey panel beside the text -
  // a store that looks unfinished. The text takes the width instead. The
  // customiser keeps the panel, as the place the merchant's picture will go.
  // A "column" layout has no photo column to stand beside here either, so the
  // points stay under the centred copy, as they always were without a photo.
  if (!section.image_url && !preview) {
    return (
      <Container width="narrow" className="text-center">
        <ImageWithTextCopy section={section} centered />
      </Container>
    );
  }
  // The points get their own column only when there are points to put in it:
  // a "column" band with none draws the two-column band it always was, rather
  // than an empty third of the width.
  const column =
    section.points_layout === 'column' && drawnImagePoints(section, includeHidden).length > 0;
  return (
    <Container
      className={cn(
        'grid items-center gap-10',
        // Photo | words | points from `lg` up. Below that the grid is one
        // column, so the three stack in source order: photo, words, points.
        column ? 'lg:grid-cols-3' : 'lg:grid-cols-2',
      )}
    >
      <div
        data-testid="image-with-text-image"
        // Clips a repositioned picture's zoom to the panel. On the right it
        // moves to the far end; the words (and the points) keep their order.
        className={cn(
          'overflow-hidden',
          section.image_side === 'right' && (column ? 'lg:order-last' : 'lg:order-2'),
        )}
        style={{ borderRadius: 'var(--storefront-radius)' }}
      >
        {section.image_url ? (
          <StorefrontImage
            url={section.image_url}
            sizes={column ? '(min-width: 1024px) 33vw, 100vw' : '(min-width: 1024px) 50vw, 100vw'}
            alt=""
            className={cn('aspect-[4/3] w-full', tileImageFit(section.image_focal))}
            style={{
              backgroundColor: 'var(--storefront-surface)',
              borderRadius: 'var(--storefront-radius)',
              ...focalStyle(section.image_focal),
            }}
          />
        ) : (
          <div
            className="aspect-[4/3] w-full"
            style={{
              backgroundColor: 'var(--storefront-surface)',
              borderRadius: 'var(--storefront-radius)',
            }}
          />
        )}
      </div>
      <div data-testid="image-with-text-copy">
        <ImageWithTextCopy section={section} withPoints={!column} />
      </div>
      {column ? <ImagePoints section={section} centered={false} asColumn /> : null}
    </Container>
  );
}

/**
 * The points a band draws: words, and shown - or hidden but in the customiser,
 * where a hidden point shows faded. Shared by the list and by the band's
 * choice of layout, so a column is never laid out for a list that draws nothing.
 */
/** A faint rule between the points of a points column. */
const POINT_RULE = 'color-mix(in srgb, var(--storefront-text) 12%, transparent)';

function drawnImagePoints(section: ImageWithTextSection, includeHidden: boolean | undefined) {
  return section.points.filter(
    (point) => point.text.trim() && (point.visible || Boolean(includeHidden)),
  );
}

function ImageWithTextCopy({
  section,
  centered = false,
  withPoints = true,
}: {
  section: ImageWithTextSection;
  /** The copy stands alone, centred, with no picture beside it. */
  centered?: boolean;
  /** False when the points stand in a column of their own beside the copy. */
  withPoints?: boolean;
}) {
  return (
    <>
      <h2 className="text-2xl tracking-tight sm:text-3xl" style={STOREFRONT_HEADING_STYLE}>
        <Editable section={section} field="heading" value={section.heading} emphasis>
          <HeadingText>{section.heading}</HeadingText>
        </Editable>
      </h2>
      {section.body ? (
        <p className="mt-4 whitespace-pre-line text-base leading-relaxed">
          <Editable section={section} field="body" value={section.body} multiline>
            {section.body}
          </Editable>
        </p>
      ) : null}
      {withPoints ? <ImagePoints section={section} centered={centered} /> : null}
      <CtaButton label={section.cta_label} href={section.cta_href} section={section} />
    </>
  );
}

/**
 * An image-with-text band's icon points, under its paragraph and above its
 * button - the reasons beside the photo, one line each. A hidden point shows
 * faded in the customiser and not at all on the shop, as a hidden block does;
 * a point with no words yet is left off both. Without the photo the copy is
 * centred, and the list stays a left-aligned column centred with it, since a
 * ragged centred list of icons reads as scattered.
 *
 * `asColumn` draws the same list as the band's third column: a card on the
 * theme's surface with hairlines between the points, the way a feature list
 * stands apart beside a paragraph, rather than more lines of the paragraph.
 */
function ImagePoints({
  section,
  centered,
  asColumn = false,
}: {
  section: ImageWithTextSection;
  centered: boolean;
  asColumn?: boolean;
}) {
  const { includeHidden } = useEnvironment();
  const points = drawnImagePoints(section, includeHidden);
  if (points.length === 0) return null;

  return (
    <ul
      data-testid="image-points"
      data-as-column={asColumn ? 'true' : undefined}
      className={cn(
        'grid text-start',
        asColumn ? 'divide-y p-6' : 'mt-6 gap-3',
        centered && 'mx-auto w-fit',
      )}
      style={
        asColumn
          ? {
              backgroundColor: 'var(--storefront-background)',
              border: `1px solid ${POINT_RULE}`,
              boxShadow: '0 1px 2px rgb(15 23 42 / 0.04), 0 12px 32px -18px rgb(15 23 42 / 0.18)',
              borderRadius: 'calc(var(--storefront-radius) + 4px)',
              // The hairlines between points: the theme's text, faint, so they
              // show on any surface colour.
              borderColor: POINT_RULE,
            }
          : undefined
      }
    >
      {points.map((point) => {
        const Icon = FEATURE_ICON_COMPONENTS[point.icon] ?? ShieldCheck;
        return (
          <li
            key={point.id}
            className={cn(
              'flex items-start gap-3',
              asColumn && 'py-4 first:pt-0 last:pb-0',
              !point.visible && 'opacity-40',
            )}
            style={asColumn ? { borderColor: POINT_RULE } : undefined}
          >
            <Icon
              className="mt-0.5 size-5 shrink-0"
              style={{ color: 'var(--storefront-primary)' }}
              aria-hidden
            />
            <span className="text-base leading-relaxed">
              <Editable section={section} field="text" blockId={point.id} value={point.text}>
                {point.text}
              </Editable>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function Banner({ section }: { section: BannerSection }) {
  return (
    <Container className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center text-sm font-medium">
      <span>
        <Editable section={section} field="text" value={section.text} emphasis>
          <HeadingText>{section.text}</HeadingText>
        </Editable>
      </span>
      {section.href && section.link_label ? (
        <SectionLink
          href={section.href}
          className="font-semibold underline"
          style={{ color: 'var(--storefront-primary)' }}
        >
          <Editable section={section} field="link_label" value={section.link_label} isolateClick>
            {section.link_label}
          </Editable>
        </SectionLink>
      ) : null}
    </Container>
  );
}

/** Grouped text links, from the shop's categories or the merchant's own groups. */
function LinkDirectoryBand({ section }: { section: LinkDirectorySection }) {
  const { categories } = useEnvironment();
  const groups = linkDirectoryGroups(section, categories);
  if (groups.length === 0) {
    return (
      <Container>
        <PreviewHint>
          {section.source === 'manual'
            ? 'Add a link group with a heading and a few links, and it appears here.'
            : 'Add categories to your shop and their sub-categories are listed here.'}{' '}
          Until then this section is hidden on your shop.
        </PreviewHint>
      </Container>
    );
  }
  return (
    <LinkDirectoryView
      section={section}
      groups={groups}
      heading={<SectionHeading section={section} title={section.title} className="mb-4" />}
      renderLink={(href, className, children, style) => (
        <SectionLink href={href} className={className} style={style}>
          {children}
        </SectionLink>
      )}
    />
  );
}

function Text({ section }: { section: TextSection }) {
  // Merchant-authored markup, sanitised on the way in by the editor and again
  // here: what reaches a shopper is never trusted to have stayed clean in the
  // database.
  const html = sanitizeRichText(section.body);
  const { editText } = useEnvironment();
  const bodyHandle =
    editText && !isPlaceholderId(section.id) ? editText(section, 'body') : null;

  return (
    <Container width="narrow" className={cn(section.align === 'center' && 'text-center')}>
      {section.title ? (
        <h2 className="text-2xl tracking-tight sm:text-3xl" style={STOREFRONT_HEADING_STYLE}>
          <Editable section={section} field="title" value={section.title} emphasis>
            <HeadingText>{section.title}</HeadingText>
          </Editable>
        </h2>
      ) : null}
      {/* The body is formatted writing, so it is edited as such - see
          `EditableRichText` - rather than as the plain words the title is. */}
      {html ? (
        <EditableRichText
          handle={bodyHandle}
          html={section.body}
          className="prose-storefront mt-4 text-base leading-relaxed"
        />
      ) : (
        <PreviewHint>Write something here.</PreviewHint>
      )}
    </Container>
  );
}

function Features({ section }: { section: FeaturesSection }) {
  const { includeHidden } = useEnvironment();
  const promises = shownBlocks(section.blocks, includeHidden);

  if (promises.length === 0) {
    return (
      <Container>
        <PreviewHint>Add a promise to fill this strip.</PreviewHint>
      </Container>
    );
  }

  return (
    <Container>
      <SectionHeading section={section} title={section.title} subtitle={section.subtitle} />
      <div className={cn('grid gap-8', columnClass(section.columns))} data-features-style={section.style}>
        {promises.map((block) => {
          const Icon = FEATURE_ICON_COMPONENTS[block.icon];
          // The stats style leads with the figure, drawn in the heading font
          // and the theme's colour; a block with no figure yet keeps its icon
          // so the row never shows a hole where the number goes.
          const stat = section.style === 'stats' ? block.stat.trim() : '';
          return (
            <BlockFrame key={block.id} section={section} block={block}>
                <div className="text-center">
                {stat ? (
                  <p
                    data-feature-stat
                    className="text-4xl leading-none tracking-tight sm:text-5xl"
                    style={{ ...STOREFRONT_HEADING_STYLE, color: 'var(--storefront-primary)' }}
                  >
                    <Editable section={section} field="stat" blockId={block.id} value={block.stat}>
                      {block.stat}
                    </Editable>
                  </p>
                ) : (
                  <Icon
                    className="mx-auto size-7"
                    style={{ color: 'var(--storefront-primary)' }}
                    aria-hidden
                  />
                )}
                <p className={cn('text-sm font-semibold', stat ? 'mt-2' : 'mt-3')}>
                  <Editable section={section} field="title" blockId={block.id} value={block.title}>
                    {block.title}
                  </Editable>
                </p>
                {block.body ? (
                  <p className="mt-1 text-sm" style={{ color: 'var(--storefront-muted)' }}>
                    <Editable
                      section={section}
                      field="body"
                      blockId={block.id}
                      value={block.body}
                      multiline
                    >
                      {block.body}
                    </Editable>
                  </p>
                ) : null}
              </div>
            </BlockFrame>
          );
        })}
      </div>
    </Container>
  );
}

function Testimonials({ section }: { section: TestimonialsSection }) {
  const { includeHidden } = useEnvironment();
  const copy = useShopContent();
  const quoted =shownBlocks(section.blocks, includeHidden).filter((block) => block.quote.trim());

  if (quoted.length === 0) {
    return (
      <Container>
        <PreviewHint>Add a review a customer actually gave you.</PreviewHint>
      </Container>
    );
  }

  return (
    <Container>
      <SectionHeading section={section} title={section.title} subtitle={section.subtitle} />
      <div className={cn('grid gap-6', columnClass(section.columns))}>
        {quoted.map((block) => (
          <BlockFrame key={block.id} section={section} block={block}>
          <figure
            className="flex h-full flex-col p-5"
            style={{
              backgroundColor: 'var(--storefront-surface)',
              borderRadius: 'var(--storefront-radius)',
            }}
          >
            {block.rating ? (
              <div className="mb-3 flex gap-0.5" aria-label={copy('section.rating', { rating: block.rating })}>
                {Array.from({ length: block.rating }, (_, index) => (
                  <Star
                    key={index}
                    className="size-4"
                    style={{ color: 'var(--storefront-primary)' }}
                    fill="currentColor"
                    aria-hidden
                  />
                ))}
              </div>
            ) : null}
            <blockquote className="text-sm leading-relaxed">
              <Editable
                section={section}
                field="quote"
                blockId={block.id}
                value={block.quote}
                multiline
              >
                {block.quote}
              </Editable>
            </blockquote>
            {block.author ? (
              <figcaption
                className="mt-4 text-sm font-semibold"
                style={{ color: 'var(--storefront-muted)' }}
              >
                <Editable section={section} field="author" blockId={block.id} value={block.author}>
                  {block.author}
                </Editable>
                {block.role ? (
                  <span className="font-normal">
                    {' · '}
                    <Editable section={section} field="role" blockId={block.id} value={block.role}>
                      {block.role}
                    </Editable>
                  </span>
                ) : null}
              </figcaption>
            ) : null}
          </figure>
          </BlockFrame>
        ))}
      </div>
    </Container>
  );
}

function Faq({ section }: { section: FaqSection }) {
  const { includeHidden, preview } = useEnvironment();
  // Narrowed to the merchant's picks from the FAQ catalog, in the blocks' own
  // order - see `pickedBlocks`.
  const answered = shownBlocks(pickedBlocks(section), includeHidden).filter((block) =>
    block.question.trim(),
  );

  if (answered.length === 0) {
    return (
      <Container>
        <PreviewHint>Add a question shoppers ask you.</PreviewHint>
      </Container>
    );
  }

  const list = (
    <>
      <SectionHeading section={section} title={section.title} />
      <dl className="divide-y" style={{ borderColor: 'var(--storefront-surface)' }}>
        {answered.map((block, index) => (
          <BlockFrame key={block.id} section={section} block={block}>
          {/* Indexed rather than `first:`, because in the customiser each row
              stands inside a selection frame and would every one of them be a
              first child. A preview that spaces its questions differently from
              the live page is the drift this whole renderer exists to avoid. */}
          <div className={cn('py-4', index === 0 && 'pt-0')}>
            <dt className="text-base font-semibold">
              <Editable section={section} field="question" blockId={block.id} value={block.question}>
                {block.question}
              </Editable>
            </dt>
            {block.answer ? (
              <dd
                className="mt-1.5 whitespace-pre-line text-sm leading-relaxed"
                style={{ color: 'var(--storefront-muted)' }}
              >
                <Editable
                  section={section}
                  field="answer"
                  blockId={block.id}
                  value={block.answer}
                  multiline
                >
                  {block.answer}
                </Editable>
              </dd>
            ) : null}
          </div>
          </BlockFrame>
        ))}
      </dl>
    </>
  );

  // No side, or a side with no photo yet on the shop: the plain list, exactly
  // as an FAQ always read. The customiser keeps the photo's place instead.
  const side = section.image_side;
  if (side === 'none' || (!section.image_url && !preview)) {
    return <Container width="narrow">{list}</Container>;
  }

  if (faqPhotoBehind(section)) {
    // The photo fills the band - the section is the box it is placed in, so
    // it covers the band's padding too - darkened so the panel reads as the
    // thing to look at, and the questions stand in a panel of the page's own
    // colours on the chosen side.
    return (
      <>
        <div aria-hidden data-faq-photo="background" className="absolute inset-0 overflow-hidden">
          {section.image_url ? (
            <StorefrontImage
              url={section.image_url}
              sizes="100vw"
              alt=""
              className="size-full object-cover"
              style={focalStyle(section.image_focal)}
            />
          ) : (
            <div className="size-full" style={{ backgroundColor: 'var(--storefront-surface)' }} />
          )}
          <div className="absolute inset-0" style={{ backgroundColor: 'rgb(0 0 0 / 0.45)' }} />
        </div>
        <Container className="relative">
          <div
            data-faq-panel={side}
            className={cn(
              'w-full max-w-[36rem] p-6 sm:p-10',
              side === 'right' ? 'ml-auto' : 'mr-auto',
            )}
            style={{ ...FAQ_PANEL_COLORS, borderRadius: 'var(--storefront-radius)' }}
          >
            {list}
          </div>
        </Container>
      </>
    );
  }

  return (
    <Container className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
      <div data-faq-photo="beside" className={cn(side === 'right' && 'lg:order-2')}>
        {section.image_url ? (
          <StorefrontImage
            url={section.image_url}
            sizes="(min-width: 1024px) 50vw, 100vw"
            alt=""
            className="aspect-[4/3] w-full object-cover lg:aspect-[4/5]"
            style={{ borderRadius: 'var(--storefront-radius)', ...focalStyle(section.image_focal) }}
          />
        ) : (
          <div
            className="aspect-[4/3] w-full lg:aspect-[4/5]"
            style={{ backgroundColor: 'var(--storefront-surface)', borderRadius: 'var(--storefront-radius)' }}
          />
        )}
      </div>
      <div className={cn(side === 'right' && 'lg:order-1')}>{list}</div>
    </Container>
  );
}

/**
 * A shop's story as dated steps.
 *
 * Vertical runs down a line at the left with a dot at each step - the About
 * page's reading order. Horizontal stands the steps side by side under one
 * line, and scrolls sideways on a phone rather than squeezing four dates into
 * a column each a few words wide. A step's picture is optional and drawn small
 * under its words, so a story with pictures on some steps still reads as one.
 */
function Timeline({ section }: { section: TimelineSection }) {
  const { includeHidden } = useEnvironment();
  const steps = shownBlocks(section.blocks, includeHidden).filter((block) => block.title.trim());

  if (steps.length === 0) {
    return (
      <Container>
        <PreviewHint>Add a milestone to tell your story.</PreviewHint>
      </Container>
    );
  }

  const horizontal = section.layout === 'horizontal';
  const dot = (
    <span
      aria-hidden
      className="block size-3 rounded-full"
      style={{ backgroundColor: 'var(--storefront-primary)' }}
    />
  );

  const step = (block: TimelineSection['blocks'][number]) => (
    <>
      <p
        className="text-sm font-semibold uppercase tracking-wide"
        style={{ color: 'var(--storefront-primary)' }}
      >
        <Editable section={section} field="date" blockId={block.id} value={block.date}>
          {block.date}
        </Editable>
      </p>
      <h3 className="mt-1 text-lg font-semibold" style={STOREFRONT_HEADING_STYLE}>
        <Editable section={section} field="title" blockId={block.id} value={block.title}>
          {block.title}
        </Editable>
      </h3>
      {block.body ? (
        <p
          className="mt-1.5 whitespace-pre-line text-sm leading-relaxed"
          style={{ color: 'var(--storefront-muted)' }}
        >
          <Editable section={section} field="body" blockId={block.id} value={block.body} multiline>
            {block.body}
          </Editable>
        </p>
      ) : null}
      {block.image_url ? (
        <StorefrontImage
          url={block.image_url}
          sizes="(min-width: 640px) 20rem, 100vw"
          alt=""
          className="mt-4 aspect-[4/3] w-full max-w-xs object-cover"
          style={{ borderRadius: 'var(--storefront-radius)' }}
        />
      ) : null}
    </>
  );

  if (horizontal) {
    return (
      <Container>
        <SectionHeading section={section} title={section.title} subtitle={section.subtitle} />
        <ol
          data-timeline-layout="horizontal"
          className="-mx-4 flex snap-x gap-8 overflow-x-auto px-4 pb-2 lg:mx-0 lg:px-0"
        >
          {steps.map((block) => (
            <BlockFrame key={block.id} section={section} block={block}>
              <li className="relative min-w-[14rem] flex-1 snap-start pt-7">
                {/* The line runs through every step's top, so side by side
                    they draw one line with a dot at each date. */}
                <span
                  aria-hidden
                  className="absolute inset-x-0 top-[5px] h-0.5"
                  style={TIMELINE_LINE}
                />
                <span className="absolute left-0 top-0">{dot}</span>
                {step(block)}
              </li>
            </BlockFrame>
          ))}
        </ol>
      </Container>
    );
  }

  return (
    <Container width="narrow">
      <SectionHeading section={section} title={section.title} subtitle={section.subtitle} />
      <ol data-timeline-layout="vertical" className="relative">
        {/* One line down the whole list, behind the dots. */}
        <span
          aria-hidden
          className="absolute bottom-2 left-[5px] top-2 w-0.5"
          style={TIMELINE_LINE}
        />
        {steps.map((block, index) => (
          <BlockFrame key={block.id} section={section} block={block}>
            {/* Indexed rather than `last:` for the reason the FAQ gives: in the
                customiser each step stands inside its own selection frame. */}
            <li className={cn('relative pl-8', index < steps.length - 1 && 'pb-10')}>
              <span className="absolute left-0 top-1.5">{dot}</span>
              {step(block)}
            </li>
          </BlockFrame>
        ))}
      </ol>
    </Container>
  );
}

/**
 * A timeline's line: the band's own muted text colour, faded, so it reads on a
 * plain, a surface or a coloured band alike - a fixed surface grey would vanish
 * into a surface band.
 */
const TIMELINE_LINE: CSSProperties = { backgroundColor: 'var(--storefront-muted)', opacity: 0.3 };

/** The page's own colours for the panel an FAQ's questions stand in over its photo. */
const FAQ_PANEL_COLORS: CSSProperties = colorScope('background', 'text') ?? {};

/**
 * Whether an FAQ draws its photo behind the whole band, which needs the band
 * to be the box the photo is placed against. A side with no photo yet is the
 * plain list on the shop, so only a photo - or the customiser - counts.
 */
function faqPhotoBehind(section: Section, preview = true): boolean {
  return (
    section.type === 'faq' &&
    section.image_side !== 'none' &&
    section.image_style === 'background' &&
    (Boolean(section.image_url) || preview)
  );
}

function Gallery({ section }: { section: GallerySection }) {
  const { includeHidden } = useEnvironment();
  const images = shownBlocks(section.blocks, includeHidden).filter((block) => block.image_url);

  if (images.length === 0) {
    return (
      <Container>
        <PreviewHint>Upload an image to start this gallery.</PreviewHint>
      </Container>
    );
  }

  // The button under the pictures: only once it has both words and a link,
  // which is the merchant's own profile - so it opens in its own tab.
  const cta =
    section.cta_label && section.href ? (
      <div className="text-center">
        <SectionLink
          href={section.href}
          newTab
          className="mt-7 inline-flex h-12 items-center px-7 text-sm font-semibold"
          style={STOREFRONT_BUTTON_STYLE}
        >
          <Editable section={section} field="cta_label" value={section.cta_label} isolateClick>
            {section.cta_label}
          </Editable>
        </SectionLink>
      </div>
    ) : null;

  if (section.style === 'strip') {
    return <GalleryStrip section={section} images={images} cta={cta} />;
  }

  return (
    <Container>
      <SectionHeading section={section} title={section.title} />
      <div className={cn('grid gap-4', columnClass(section.columns))}>
        {images.map((block) => (
          <BlockFrame key={block.id} section={section} block={block}>
          <figure>
            <SectionLink
              href={block.href}
              // Clips a repositioned picture's zoom to its square.
              className="group block overflow-hidden"
              style={{ borderRadius: 'var(--storefront-radius)' }}
            >
              <StorefrontImage
                url={block.image_url}
                sizes={gridImageSizes(section.columns)}
                alt={block.caption || ''}
                className={cn(
                  'aspect-square w-full',
                  tileImageFit(block.image_focal),
                  'transition-transform group-hover:scale-[1.02]',
                )}
                style={{
                  backgroundColor: 'var(--storefront-surface)',
                  borderRadius: 'var(--storefront-radius)',
                  ...focalStyle(block.image_focal),
                }}
              />
            </SectionLink>
            {block.caption ? (
              <figcaption className="mt-2 text-xs" style={{ color: 'var(--storefront-muted)' }}>
                <Editable section={section} field="caption" blockId={block.id} value={block.caption}>
                  {block.caption}
                </Editable>
              </figcaption>
            ) : null}
          </figure>
          </BlockFrame>
        ))}
      </div>
      {cta}
    </Container>
  );
}

/** A strip's tiles across from a tablet up, pre-resolved so Tailwind can see them. */
const STRIP_COLUMNS: Record<number, string> = {
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
  4: 'md:grid-cols-4',
  5: 'md:grid-cols-5',
  6: 'md:grid-cols-6',
};

/**
 * A gallery as one row of square photos with hairline gaps - the social feed
 * row under a shop's home page. As many tiles as the gallery's columns, the
 * rest of its pictures left off; on a phone the row scrolls sideways, inside
 * its own box, so the page never grows wider. `full_bleed` runs the row from
 * one edge of the window to the other, under a heading still in the column.
 */
function GalleryStrip({
  section,
  images,
  cta,
}: {
  section: GallerySection;
  images: GallerySection['blocks'];
  cta: ReactNode;
}) {
  const columns = Math.min(Math.max(section.columns, 2), 6);
  const shown = images.slice(0, columns);
  const radius = section.full_bleed ? undefined : 'var(--storefront-radius)';
  const sizes = section.full_bleed
    ? `(min-width: 768px) ${Math.round(100 / columns)}vw, 42vw`
    : gridImageSizes(columns);
  const row = (
    <div
      data-gallery-strip={columns}
      className={cn(
        'flex snap-x snap-mandatory gap-1 overflow-x-auto [scrollbar-width:none]',
        'md:grid md:snap-none md:overflow-visible',
        STRIP_COLUMNS[columns],
      )}
    >
      {shown.map((block) => (
        <div key={block.id} data-gallery-tile className="w-[42%] shrink-0 snap-start sm:w-[30%] md:w-auto">
          <BlockFrame section={section} block={block}>
            <SectionLink
              href={block.href}
              // Clips a repositioned picture's zoom to its square.
              className="group block overflow-hidden"
              style={{ borderRadius: radius }}
            >
              <StorefrontImage
                url={block.image_url}
                sizes={sizes}
                alt={block.caption || ''}
                // A social row is a run of filled squares, never letterboxed
                // pictures: each one is cropped to its tile, at its focal point.
                className="aspect-square w-full object-cover transition-transform group-hover:scale-[1.02]"
                style={{
                  backgroundColor: 'var(--storefront-surface)',
                  borderRadius: radius,
                  ...focalStyle(block.image_focal),
                }}
              />
            </SectionLink>
          </BlockFrame>
        </div>
      ))}
    </div>
  );

  if (section.full_bleed) {
    return (
      <div data-gallery-full-bleed>
        <Container>
          <SectionHeading section={section} title={section.title} />
        </Container>
        {row}
        {cta ? <Container>{cta}</Container> : null}
      </div>
    );
  }
  return (
    <Container>
      <SectionHeading section={section} title={section.title} />
      {row}
      {cta}
    </Container>
  );
}

// --- Promo grid -----------------------------------------------------------
/**
 * Each layout as grid classes: the grid's own, then one per tile in order.
 * Everything is from `md:` up - below it the grid is one column and every
 * tile one row tall, which is the stack a phone needs.
 */
const PROMO_LAYOUT_CLASSES: Record<PromoGridLayout, { grid: string; tiles: string[] }> = {
  '2-equal': { grid: 'md:grid-cols-2', tiles: [] },
  '3-equal': { grid: 'md:grid-cols-3', tiles: [] },
  '4-equal': { grid: 'md:grid-cols-2 lg:grid-cols-4', tiles: [] },
  '1-large-2-small': { grid: 'md:grid-cols-2', tiles: ['md:row-span-2'] },
  '1-large-4-small': {
    grid: 'md:grid-cols-2 lg:grid-cols-4',
    tiles: ['md:col-span-2 md:row-span-2'],
  },
  '2-large-3-small': {
    grid: 'md:grid-cols-6',
    tiles: ['md:col-span-3', 'md:col-span-3', 'md:col-span-2', 'md:col-span-2', 'md:col-span-2'],
  },
  // Drawn by `PromoRow` instead; here so the record stays complete.
  row: { grid: '', tiles: [] },
};

/** A row's columns on a computer screen, by how many cards it holds. */
const PROMO_ROW_COLUMNS: Record<number, string> = {
  1: 'lg:grid-cols-1',
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
  5: 'lg:grid-cols-5',
  6: 'lg:grid-cols-6',
};

/** The equal layout for a grid holding fewer tiles than its layout shows. */
const PROMO_EQUAL: Record<number, PromoGridLayout> = { 2: '2-equal', 3: '3-equal', 4: '4-equal' };

/** One row's height, by the section's `height`; a phone's rows are a little shorter. */
const PROMO_ROW: Record<PromoGridSection['height'], string> = {
  sm: '[--promo-row:10rem] md:[--promo-row:12rem]',
  md: '[--promo-row:13rem] md:[--promo-row:16rem]',
  lg: '[--promo-row:16rem] md:[--promo-row:21rem]',
};

const PROMO_GAP: Record<PromoGridSection['gap'], string> = {
  none: 'gap-0',
  sm: 'gap-2',
  md: 'gap-4',
  lg: 'gap-6',
};

/** Where a tile's words sit: vertical, then horizontal. */
const PROMO_POSITION: Record<PromoTileBlock['text_position'], string> = {
  'top-left': 'justify-start items-start text-left',
  'top-center': 'justify-start items-center text-center',
  'top-right': 'justify-start items-end text-right',
  'center-left': 'justify-center items-start text-left',
  center: 'justify-center items-center text-center',
  'center-right': 'justify-center items-end text-right',
  'bottom-left': 'justify-end items-start text-left',
  'bottom-center': 'justify-end items-center text-center',
  'bottom-right': 'justify-end items-end text-right',
};

/**
 * The colours a tile with no picture takes, in turn, with the text that reads
 * on each - so a fresh grid is a set of the theme's own colours rather than
 * grey holes, and it repaints with the palette.
 */
const PROMO_PLAIN: ReadonlyArray<{ background: string; text: string }> = [
  { background: 'var(--storefront-palette-primary, var(--storefront-primary))', text: 'var(--storefront-palette-background, var(--storefront-background))' },
  { background: 'var(--storefront-palette-surface, var(--storefront-surface))', text: 'var(--storefront-palette-text, var(--storefront-text))' },
  { background: 'var(--storefront-palette-text, var(--storefront-text))', text: 'var(--storefront-palette-background, var(--storefront-background))' },
  { background: 'var(--storefront-palette-accent, var(--storefront-palette-primary, var(--storefront-primary)))', text: 'var(--storefront-palette-background, var(--storefront-background))' },
];

/** The text that reads on a colour the merchant chose for a plain tile. */
function promoTextOn(choice: string): string {
  if (isHexColor(choice)) {
    return contrastRatio(choice, '#ffffff') >= 3 ? '#ffffff' : '#111111';
  }
  return choice === 'surface' || choice === 'background'
    ? 'var(--storefront-palette-text, var(--storefront-text))'
    : 'var(--storefront-palette-background, var(--storefront-background))';
}

/** A tile's backdrop and text colour, from its picture or its colour. */
function promoTileColors(
  block: PromoTileBlock,
  index: number,
): { background: string; text: string } {
  const own = resolveColor(block.text_color);
  if (block.image_url) {
    return { background: 'var(--storefront-surface)', text: own ?? '#ffffff' };
  }
  const chosen = resolveColor(block.background_color);
  if (chosen && block.background_color) {
    return { background: chosen, text: own ?? promoTextOn(block.background_color) };
  }
  const plain = PROMO_PLAIN[index % PROMO_PLAIN.length];
  return { background: plain.background, text: own ?? plain.text };
}

/** The layout a grid of `count` tiles is actually drawn in. */
function promoLayoutFor(layout: PromoGridLayout, count: number): PromoGridLayout {
  // A row is a row at any length: it never falls back to a mosaic.
  if (layout === 'row' || count >= PROMO_LAYOUT_TILES[layout]) return layout;
  return PROMO_EQUAL[Math.min(Math.max(count, 2), 4)];
}

function PromoTile({
  section,
  block,
  index,
  className,
}: {
  section: PromoGridSection;
  block: PromoTileBlock;
  index: number;
  className?: string;
}) {
  const colors = promoTileColors(block, index);
  return (
    <BlockFrame section={section} block={block}>
      <SectionLink
        href={block.href}
        className={cn('group relative flex min-h-[var(--promo-row)] overflow-hidden', className)}
        style={{
          backgroundColor: colors.background,
          color: colors.text,
          borderRadius: 'var(--storefront-radius)',
        }}
      >
        {block.image_url ? (
          <>
            <StorefrontImage
              url={block.image_url}
              sizes="(min-width: 768px) 50vw, 100vw"
              alt=""
              className="absolute inset-0 size-full object-cover transition-transform duration-500 group-hover:scale-105"
              // The hover's `scale` is its own property in Tailwind 4, so it
              // enlarges on top of a chosen zoom rather than replacing it.
              style={focalStyle(block.image_focal)}
            />
            <span
              aria-hidden
              className="absolute inset-0"
              style={{ backgroundColor: `rgba(0, 0, 0, ${block.overlay_opacity / 100})` }}
            />
          </>
        ) : null}
        <span
          className={cn(
            'relative flex w-full flex-col gap-1.5 p-5 sm:p-7',
            PROMO_POSITION[block.text_position],
          )}
        >
          {block.eyebrow ? (
            <span className="text-xs font-semibold uppercase tracking-widest opacity-90">
              <Editable section={section} field="eyebrow" blockId={block.id} value={block.eyebrow}>
                {block.eyebrow}
              </Editable>
            </span>
          ) : null}
          {block.heading ? (
            <span className="max-w-md text-xl leading-tight sm:text-2xl" style={STOREFRONT_HEADING_STYLE}>
              <Editable section={section} field="heading" blockId={block.id} value={block.heading} emphasis>
                <HeadingText>{block.heading}</HeadingText>
              </Editable>
            </span>
          ) : null}
          {block.subheading ? (
            <span className="max-w-md text-sm opacity-90">
              <Editable section={section} field="subheading" blockId={block.id} value={block.subheading}>
                {block.subheading}
              </Editable>
            </span>
          ) : null}
          {block.cta_label ? (
            <span className="mt-2 inline-flex items-center gap-1 border-b-2 border-current pb-0.5 text-sm font-semibold">
              <Editable
                section={section}
                field="cta_label"
                blockId={block.id}
                value={block.cta_label}
                isolateClick
              >
                {block.cta_label}
              </Editable>
              <span aria-hidden>→</span>
            </span>
          ) : null}
        </span>
      </SectionLink>
    </BlockFrame>
  );
}

/**
 * A mosaic of offer tiles. A tile with no picture is a block of one of the
 * theme's colours, so a grid added a moment ago already looks designed.
 */
function PromoGrid({ section }: { section: PromoGridSection }) {
  const { includeHidden, preview } = useEnvironment();
  const all = shownBlocks(section.blocks, includeHidden);
  const room = PROMO_LAYOUT_TILES[section.layout];
  const tiles = all.slice(0, room);

  if (tiles.length === 0) {
    return (
      <Container>
        <PreviewHint>Add a tile to start this promo grid.</PreviewHint>
      </Container>
    );
  }

  const layout = promoLayoutFor(section.layout, tiles.length);
  const classes = PROMO_LAYOUT_CLASSES[layout];

  if (layout === 'row') {
    // Narrow, tall cards in one line. On a computer they share the width
    // equally; below that each keeps a card's width and the line scrolls
    // sideways, snapping to a card, rather than stacking into a tall column.
    return (
      <Container>
        <SectionHeading section={section} title={section.title} subtitle={section.subtitle} />
        <div
          data-layout="row"
          className={cn(
            'flex snap-x snap-mandatory overflow-x-auto pb-2 lg:grid lg:overflow-visible lg:pb-0',
            PROMO_ROW_COLUMNS[tiles.length],
            PROMO_GAP[section.gap],
            PROMO_ROW[section.height],
          )}
        >
          {tiles.map((block, index) => (
            <div
              key={block.id}
              data-promo-card
              className="w-[70%] shrink-0 snap-start sm:w-[42%] md:w-[30%] lg:w-auto"
            >
              <PromoTile
                section={section}
                block={block}
                index={index}
                className="h-full min-h-[calc(var(--promo-row)*1.6)]"
              />
            </div>
          ))}
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <SectionHeading section={section} title={section.title} subtitle={section.subtitle} />
      <div
        data-layout={layout}
        className={cn('grid grid-cols-1', classes.grid, PROMO_GAP[section.gap], PROMO_ROW[section.height])}
        style={{ gridAutoRows: 'minmax(var(--promo-row), auto)' }}
      >
        {tiles.map((block, index) => (
          <PromoTile
            key={block.id}
            section={section}
            block={block}
            index={index}
            className={classes.tiles[index]}
          />
        ))}
      </div>
      {preview && all.length > room ? (
        <p className="mt-3 text-xs" style={{ color: 'var(--storefront-muted)' }}>
          This layout shows {room} tiles; the other {all.length - room} are not shown.
        </p>
      ) : null}
    </Container>
  );
}

// --- Brand strip ----------------------------------------------------------
const BRAND_LOGO_HEIGHT: Record<BrandStripSection['logo_height'], string> = {
  sm: 'h-8',
  md: 'h-12',
  lg: 'h-16',
};

const BRAND_WORDMARK_SIZE: Record<BrandStripSection['logo_height'], string> = {
  sm: 'text-base',
  md: 'text-xl',
  lg: 'text-2xl',
};

const BRAND_CAROUSEL_ACROSS =
  '[--rail-across:2] sm:[--rail-across:3] md:[--rail-across:4] lg:[--rail-across:6] [--rail-gap:1rem] lg:[--rail-gap:2rem]';

/** One brand as the strip draws it, whichever source it came from. */
interface BrandItem {
  key: string;
  name: string;
  image: string;
  href: string;
  block?: BrandStripSection['blocks'][number];
}

/** Where a brand with no link of its own leads: the listing narrowed to it. */
function brandListingHref(brand: string): string {
  return `/products?brand=${encodeURIComponent(brand)}`;
}

/**
 * What the strip shows: the merchant's logos when any are uploaded, else the
 * brands on the shop's products as wordmarks, else any brand names typed in.
 */
function brandStripItems(
  section: BrandStripSection,
  brands: readonly FacetValue[],
  includeHidden = false,
): BrandItem[] {
  const blocks = shownBlocks(section.blocks, includeHidden);
  const fromBlocks = (list: typeof blocks): BrandItem[] =>
    list.map((block) => ({
      key: block.id,
      name: block.name,
      image: block.image_url,
      href: block.href || (block.name ? brandListingHref(block.name) : ''),
      block,
    }));
  if (blocks.some((block) => block.image_url)) {
    return fromBlocks(blocks.filter((block) => block.image_url || block.name.trim()));
  }
  if (brands.length > 0) {
    return brands.slice(0, 16).map((brand) => ({
      key: `brand:${brand.value}`,
      name: brand.label || brand.value,
      image: '',
      href: brandListingHref(brand.value),
    }));
  }
  return fromBlocks(blocks.filter((block) => block.name.trim()));
}

function BrandMark({ section, item }: { section: BrandStripSection; item: BrandItem }) {
  const tone = section.grayscale
    ? 'grayscale opacity-70 transition group-hover:grayscale-0 group-hover:opacity-100'
    : 'transition-opacity group-hover:opacity-80';
  const mark = (
    <SectionLink
      href={item.href}
      label={item.name || undefined}
      className="group flex h-full items-center justify-center px-2"
    >
      {item.image ? (
        // No fade: the tone dims a logo with its own opacity, which a fade
        // ending at full opacity would snap back to. No backdrop behind a
        // transparent logo either.
        <StorefrontImage
          url={item.image}
          sizes="160px"
          fade={false}
          placeholder={false}
          alt={item.name}
          className={cn('w-auto max-w-[10rem] object-contain', BRAND_LOGO_HEIGHT[section.logo_height], tone)}
        />
      ) : (
        <span
          className={cn(
            'whitespace-nowrap font-semibold tracking-tight',
            BRAND_WORDMARK_SIZE[section.logo_height],
            section.grayscale ? 'opacity-60 transition-opacity group-hover:opacity-100' : undefined,
          )}
          style={STOREFRONT_HEADING_STYLE}
        >
          {item.block ? (
            <Editable section={section} field="name" blockId={item.block.id} value={item.name}>
              {item.name}
            </Editable>
          ) : (
            item.name
          )}
        </span>
      )}
    </SectionLink>
  );
  return item.block ? (
    <BlockFrame section={section} block={item.block}>
      {mark}
    </BlockFrame>
  ) : (
    mark
  );
}

/** A row of brand logos, or the shop's own brands by name. */
function BrandStrip({ section }: { section: BrandStripSection }) {
  const { brands = [], includeHidden } = useEnvironment();
  const items = brandStripItems(section, brands, includeHidden);

  if (items.length === 0) {
    return (
      <Container>
        <PreviewHint>
          Upload brand logos, or add a brand to your products, and they appear here.
        </PreviewHint>
      </Container>
    );
  }

  if (section.style === 'carousel') {
    return (
      <Container>
        <Rail
          label={section.title || 'Brands'}
          itemCount={items.length}
          across={BRAND_CAROUSEL_ACROSS}
          arrows="bare"
          heading={
            <SectionHeading
              section={section}
              title={section.title}
              subtitle={section.subtitle}
              className="mb-4"
            />
          }
        >
          {items.map((item) => (
            <li key={item.key} className={cn(RAIL_ITEM, 'flex min-h-20 items-center justify-center')}>
              <BrandMark section={section} item={item} />
            </li>
          ))}
        </Rail>
      </Container>
    );
  }

  return (
    <Container>
      <SectionHeading section={section} title={section.title} subtitle={section.subtitle} />
      {section.style === 'grid' ? (
        <ul className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {items.map((item) => (
            <li
              key={item.key}
              className="flex min-h-24 items-center justify-center p-4"
              style={{
                backgroundColor: 'var(--storefront-surface)',
                borderRadius: 'var(--storefront-radius)',
              }}
            >
              <BrandMark section={section} item={item} />
            </li>
          ))}
        </ul>
      ) : (
        <ul className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6">
          {items.map((item) => (
            <li key={item.key} className="flex items-center">
              <BrandMark section={section} item={item} />
            </li>
          ))}
        </ul>
      )}
    </Container>
  );
}

/**
 * One video, in a player a shopper can actually work.
 *
 * **Two players, one frame.** YouTube is an iframe and an uploaded file is a
 * `<video>`; they share the wrapper, which is what makes the aspect ratio, the
 * rounding and the responsive behaviour one implementation rather than two
 * that drift. The wrapper carries the ratio and the player fills it
 * absolutely, so the frame shrinks with whatever column it is in - a
 * full-width band, a half-width row beside a contact form, or a phone - with
 * no breakpoint named and nothing to keep in step.
 *
 * **Controls are not negotiable.** Both players are drawn with the browser's
 * own: play/pause, volume, a seek bar and fullscreen, plus picture-in-picture
 * and casting where the platform offers them. There is no setting to take them
 * away, because a video a shopper cannot pause or mute is a thing done to
 * them - and on a phone, on a metered connection, it is an expensive one.
 *
 * **Autoplay is muted, always.** Every browser blocks an unmuted autoplay, so
 * a merchant who ticked autoplay and left the sound on would otherwise get a
 * player that silently refuses to start. Forcing `muted` makes the toggle do
 * what it says; the inspector says so too, rather than leaving it to be
 * discovered.
 *
 * **`preload="metadata"`** rather than `auto`: enough to draw the duration and
 * the seek bar, without pulling megabytes of video down for a shopper who
 * scrolled past it. `playsInline` keeps iOS from hijacking the page into its
 * own fullscreen player the moment the clip starts.
 */
function Video({ section }: { section: VideoSection }) {
  const { preview } = useEnvironment();
  const copy = useShopContent();
  const videoId = section.source === 'youtube' ? youtubeVideoId(section.youtube_url) : '';
  const fileUrl = section.source === 'upload' ? section.video_url : '';

  // Every browser blocks an unmuted autoplay; honouring the pair together is
  // what keeps the autoplay toggle from being a switch that does nothing.
  const muted = section.muted || section.autoplay;

  // The overlay gives way once the shopper starts the video, so it never sits
  // on a film they are watching. A clip that plays itself is the design the
  // words were written over, so they stay on it.
  const [started, setStarted] = useState(false);
  const frameRef = useRef<HTMLDivElement>(null);
  const hasWords = Boolean(
    section.overlay_heading || section.overlay_subheading || section.overlay_cta_label,
  );
  const overlay =
    section.overlay_text_position !== 'none' && hasWords && !(started && !section.autoplay)
      ? section.overlay_text_position
      : null;

  useEffect(() => {
    // A YouTube frame says nothing to the page when it plays; a tap into it
    // moves focus into the frame, which blurs this window - that is the start.
    if (!overlay || !videoId) return undefined;
    const onBlur = () => {
      const active = document.activeElement;
      if (active instanceof HTMLIFrameElement && frameRef.current?.contains(active)) {
        setStarted(true);
      }
    };
    window.addEventListener('blur', onBlur);
    return () => window.removeEventListener('blur', onBlur);
  }, [overlay, videoId]);

  if (!videoId && !fileUrl) {
    return (
      <Container>
        <SectionHeading section={section} title={section.title} subtitle={section.subtitle} />
        {/* The words over the player, on an empty frame, so the merchant can
            judge them before the video is in - in the customiser only. */}
        {preview && overlay ? (
          <div
            className="relative mb-4 w-full overflow-hidden"
            style={{
              aspectRatio: VIDEO_ASPECT_CSS[section.aspect_ratio],
              backgroundColor: 'var(--storefront-surface)',
              borderRadius: section.rounded ? VIDEO_ROUNDED_RADIUS : 'var(--storefront-radius)',
            }}
          >
            <VideoOverlay section={section} position={overlay} />
          </div>
        ) : null}
        <PreviewHint>
          {section.source === 'youtube'
            ? 'Paste a YouTube link to show a video here.'
            : 'Upload a video file to show it here.'}
        </PreviewHint>
      </Container>
    );
  }

  return (
    <Container>
      <SectionHeading section={section} title={section.title} subtitle={section.subtitle} />
      <div
        ref={frameRef}
        className="relative w-full overflow-hidden"
        style={{
          aspectRatio: VIDEO_ASPECT_CSS[section.aspect_ratio],
          backgroundColor: 'var(--storefront-surface)',
          borderRadius: section.rounded ? VIDEO_ROUNDED_RADIUS : 'var(--storefront-radius)',
        }}
      >
        {videoId ? (
          <iframe
            src={youtubeEmbedUrl(videoId, {
              autoplay: section.autoplay,
              loop: section.loop,
              muted,
            })}
            // Named for a screen reader, which otherwise announces an unlabelled
            // frame. The section's own heading where it has one, because that is
            // what the merchant called this video.
            title={section.title || copy('section.video')}
            className="absolute inset-0 size-full border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading="lazy"
          />
        ) : (
          // No <track>: the merchant supplies the file, and we have no
          // captions to attach to it.
          <video
            key={fileUrl}
            src={fileUrl}
            controls
            playsInline
            preload="metadata"
            autoPlay={section.autoplay}
            loop={section.loop}
            muted={muted}
            onPlay={() => setStarted(true)}
            className="absolute inset-0 size-full object-contain"
          />
        )}
        {overlay ? (
          <VideoOverlay section={section} position={overlay} />
        ) : null}
      </div>
    </Container>
  );
}

/** Corners for a video drawn `rounded`: generous, and never less than the theme's. */
const VIDEO_ROUNDED_RADIUS = 'max(var(--storefront-radius, 0px), 1.75rem)';

/** Where a video's words sit over its frame: vertical, then horizontal. */
const VIDEO_OVERLAY_PLACE: Record<Exclude<VideoOverlayPosition, 'none'>, string> = {
  'bottom-left': 'justify-end items-start text-left',
  center: 'justify-center items-center text-center',
  'bottom-center': 'justify-end items-center text-center',
};

/** The shade behind the words, darkest where they stand. */
const VIDEO_OVERLAY_SHADE: Record<Exclude<VideoOverlayPosition, 'none'>, string> = {
  'bottom-left':
    'linear-gradient(to top right, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.25) 45%, rgba(0,0,0,0) 70%)',
  center: 'radial-gradient(ellipse at center, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 70%)',
  'bottom-center': 'linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0) 60%)',
};

/**
 * A video's promotional words, over its frame.
 *
 * The layer takes no taps - `pointer-events: none` - so the player's own play
 * button and controls under it work as if it were not there; only the button
 * takes one. It also stands clear of the control bar along the bottom, so the
 * button never sits on the seek bar.
 */
function VideoOverlay({
  section,
  position,
}: {
  section: VideoSection;
  position: Exclude<VideoOverlayPosition, 'none'>;
}) {
  return (
    <div
      data-video-overlay={position}
      className={cn(
        'pointer-events-none absolute inset-0 flex flex-col gap-2 px-5 pt-5 pb-14 text-white sm:px-8 sm:pt-8 sm:pb-16',
        VIDEO_OVERLAY_PLACE[position],
      )}
      style={{ backgroundImage: VIDEO_OVERLAY_SHADE[position] }}
    >
      {section.overlay_heading ? (
        <p className="max-w-xl text-2xl leading-tight sm:text-4xl" style={STOREFRONT_HEADING_STYLE}>
          <HeadingText>{section.overlay_heading}</HeadingText>
        </p>
      ) : null}
      {section.overlay_subheading ? (
        <p className="max-w-lg text-sm opacity-90 sm:text-base">{section.overlay_subheading}</p>
      ) : null}
      {section.overlay_cta_label ? (
        <SectionLink
          href={section.overlay_cta_href || '/products'}
          className="pointer-events-auto mt-2 inline-flex h-11 items-center px-6 text-sm font-semibold"
          style={STOREFRONT_BUTTON_STYLE}
        >
          {section.overlay_cta_label}
        </SectionLink>
      ) : null}
    </div>
  );
}

/**
 * How a row of icons sits in the band it is given.
 *
 * `flex-wrap` and nothing else responsive: the row is however many icons the
 * merchant configured, and on a narrow screen the ones that do not fit move to
 * a second line at the same size rather than shrinking. Seven marks squeezed
 * into a phone's width would be seven things too small to tap, which is the
 * failure this section would otherwise have on the device most shoppers use.
 */
const SOCIAL_JUSTIFY: Record<SocialLinksSection['align'], string> = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
};

/**
 * One profile link, as the network's mark inside a tap target.
 *
 * The colours are worked out here rather than in classes because all four of
 * them come from the theme's CSS variables or the network's own palette, and
 * Tailwind cannot see either. Three styles, and what each one paints:
 *
 * - `plain` - the mark, in the brand's colour or the page's ink.
 * - `outlined` - the same mark in a ring of the same colour, which is what
 *   makes a row read as a set of buttons rather than as scattered logos.
 * - `filled` - the mark knocked out of a solid disc. The glyph then has to be
 *   the *page* colour rather than the ink, or it disappears into the disc.
 *
 * The box is at least 36px square at the smallest size and the anchor is the
 * box, not the glyph: a link a thumb misses is a link that does not work, and
 * it is the one thing about this section that a desktop preview will never
 * show the merchant.
 */
function SocialLink({
  block,
  section,
}: {
  block: SocialLinkBlock;
  section: SocialLinksSection;
}) {
  const href = socialLinkHref(block.platform, block.url);
  const label = socialLinkLabel(block);
  const { glyph, box } = SOCIAL_ICON_PIXELS[section.icon_size];

  const accent = section.brand_colors
    ? SOCIAL_PLATFORM_COLORS[block.platform]
    : 'var(--storefront-primary)';
  const filled = section.icon_style === 'filled';

  const frame: CSSProperties = {
    width: box,
    height: box,
    color: filled ? 'var(--storefront-background)' : accent,
    backgroundColor: filled ? accent : undefined,
    borderColor: section.icon_style === 'outlined' ? accent : undefined,
    borderWidth: section.icon_style === 'outlined' ? 1 : undefined,
    // A circle whatever the theme's rounding is. A social icon is round
    // everywhere a shopper has ever seen one, and a square Instagram mark
    // reads as a broken image rather than as this shop's house style.
    borderRadius: section.icon_style === 'plain' ? undefined : '9999px',
  };

  return (
    <SectionLink
      href={href}
      // The mark alone is not a name. Without this the link is announced as
      // "link" and nothing else, and a merchant who labelled a custom row gets
      // that label read out instead of "Other".
      label={label}
      // The shopper did not ask to leave the shop. Every storefront platform
      // opens its social icons this way, and a half-filled basket is what is
      // lost when they do not.
      newTab
      className="inline-flex shrink-0 items-center justify-center transition-opacity hover:opacity-75"
      style={frame}
    >
      <SocialIcon platform={block.platform} size={glyph} />
    </SectionLink>
  );
}

/**
 * Where the shop is, on the networks it is actually on.
 *
 * **A row with no address is not a row.** The section arrives carrying every
 * common network so the merchant has somewhere to paste each address; the ones
 * they never fill in are dropped here, by `socialLinkHref` returning empty. So
 * the storefront shows the profiles the shop has and says nothing about the
 * ones it does not - which is the whole reason it is safe to default the list
 * to seven networks in the first place.
 *
 * **The customiser sees more than the shopper.** `includeHidden` keeps the
 * disabled rows in so the merchant can select and re-enable them, and the
 * unconfigured ones are still filtered out - because an icon linking nowhere
 * is not something to preview, it is the thing they are about to fix.
 */
function SocialLinks({ section }: { section: SocialLinksSection }) {
  const { includeHidden } = useEnvironment();
  const linked = shownBlocks(section.blocks, includeHidden).filter((block) =>
    socialLinkHref(block.platform, block.url),
  );

  if (linked.length === 0) {
    return (
      <Container>
        <SectionHeading section={section} title={section.title} subtitle={section.subtitle} />
        <PreviewHint>
          Paste the address of a profile to show its icon here. Rows you leave empty
          are not shown to shoppers.
        </PreviewHint>
      </Container>
    );
  }

  return (
    <Container>
      <SectionHeading section={section} title={section.title} subtitle={section.subtitle} />
      <div className={cn('flex flex-wrap items-center gap-3', SOCIAL_JUSTIFY[section.align])}>
        {linked.map((block) => (
          <BlockFrame key={block.id} section={section} block={block}>
            <SocialLink block={block} section={section} />
          </BlockFrame>
        ))}
      </div>
    </Container>
  );
}

/** One field of the contact form, styled from the theme rather than Tailwind. */
function ContactField({
  label,
  className,
  multiline = false,
  ...props
}: {
  label: string;
  className?: string;
  multiline?: boolean;
} & React.InputHTMLAttributes<HTMLInputElement> &
  React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  // The label is the placeholder, as in the design, so it is also given to
  // assistive technology - a form whose fields announce themselves as nothing
  // is unusable with a screen reader however it looks.
  const style = {
    borderColor: 'var(--storefront-surface)',
    borderRadius: 'var(--storefront-radius)',
    backgroundColor: 'var(--storefront-background)',
    color: 'var(--storefront-text)',
  };

  return multiline ? (
    <textarea
      {...props}
      rows={8}
      aria-label={label}
      placeholder={label}
      className={cn('w-full border px-4 py-3 text-sm', className)}
      style={style}
    />
  ) : (
    <input
      {...props}
      aria-label={label}
      placeholder={label}
      className={cn('h-14 w-full border px-4 text-sm', className)}
      style={style}
    />
  );
}

/**
 * The contact form.
 *
 * Posts to the storefront's own contact endpoint, which emails the merchant.
 * A failure is shown as one rather than swallowed into a thank-you: a shopper
 * who believes their question has been received stops asking it.
 */
function ContactForm({ section }: { section: ContactFormSection }) {
  const { preview } = useEnvironment();
  const copy = useShopContent();
  const [values, setValues] = useState({ name: '', email: '', phone: '', message: '' });
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');

  const set = (field: keyof typeof values) => (event: { target: { value: string } }) =>
    setValues((current) => ({ ...current, [field]: event.target.value }));

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (preview) return;

    setState('sending');
    try {
      await sendContactMessage({
        name: values.name.trim(),
        email: values.email.trim(),
        // Sent only where the merchant asked for them, so a field they turned
        // off cannot arrive full of whatever was typed before they did.
        phone: section.show_phone ? values.phone.trim() || undefined : undefined,
        message: section.show_comment ? values.message.trim() || undefined : undefined,
      });
      setState('done');
      setValues({ name: '', email: '', phone: '', message: '' });
    } catch {
      setState('error');
    }
  }

  return (
    <Container width="narrow">
      {section.title ? (
        <h2
          className="text-center text-2xl tracking-tight sm:text-3xl"
          style={STOREFRONT_HEADING_STYLE}
        >
          <Editable section={section} field="title" value={section.title} emphasis>
            <HeadingText>{section.title}</HeadingText>
          </Editable>
        </h2>
      ) : null}
      {section.subtitle ? (
        <p className="mt-2 text-center text-base" style={{ color: 'var(--storefront-muted)' }}>
          <Editable section={section} field="subtitle" value={section.subtitle}>
            {section.subtitle}
          </Editable>
        </p>
      ) : null}

      {state === 'done' ? (
        <p
          className={cn('text-base', section.title || section.subtitle ? 'mt-8' : '')}
          style={{ color: 'var(--storefront-text)' }}
        >
          {section.success_message}
        </p>
      ) : (
        <form
          className={cn('grid gap-4 sm:grid-cols-2', section.title || section.subtitle ? 'mt-8' : '')}
          onSubmit={submit}
          noValidate={preview}
        >
          <ContactField
            label={copy('section.field_name')}
            required
            value={values.name}
            onChange={set('name')}
          />
          <ContactField
            label={copy('checkout.field_email')}
            type="email"
            required
            value={values.email}
            onChange={set('email')}
          />
          {section.show_phone ? (
            <ContactField
              label={copy('account.phone')}
              type="tel"
              className="sm:col-span-2"
              value={values.phone}
              onChange={set('phone')}
            />
          ) : null}
          {section.show_comment ? (
            <ContactField
              label={copy('section.field_comment')}
              multiline
              className="sm:col-span-2"
              value={values.message}
              onChange={set('message')}
            />
          ) : null}

          <div className="sm:col-span-2">
            <button
              type="submit"
              disabled={state === 'sending'}
              className="h-12 px-8 text-sm font-semibold disabled:opacity-60"
              style={STOREFRONT_BUTTON_STYLE}
            >
              {state === 'sending' ? copy('account.sending') : section.button_label}
            </button>
          </div>
        </form>
      )}

      {state === 'error' ? (
        <p className="mt-3 text-sm" style={{ color: 'var(--storefront-muted)' }}>
          {copy('section.contact_error')}
        </p>
      ) : null}
    </Container>
  );
}

/** A signup field, on whatever band the section is drawn on. */
const NEWSLETTER_INPUT_STYLE: CSSProperties = {
  borderColor: 'var(--storefront-muted)',
  borderRadius: 'var(--storefront-radius)',
  backgroundColor: 'var(--storefront-background)',
  color: 'var(--storefront-text)',
};

/**
 * Email signup.
 *
 * Posts to the storefront's own subscribe endpoint, which records marketing
 * consent against a customer of this store. A form that collected addresses
 * and dropped them would be worse than no form at all, so this one is wired or
 * it is not shown.
 */
function Newsletter({ section }: { section: NewsletterSection }) {
  const { preview } = useEnvironment();
  const copy = useShopContent();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (preview || !email.trim()) return;

    setState('sending');
    try {
      // The name only when the form asks for it; blank is no name.
      const typed = section.collect_name ? name.trim() : '';
      await (typed
        ? subscribeToNewsletter(email.trim(), typed)
        : subscribeToNewsletter(email.trim()));
      setState('done');
      setEmail('');
      setName('');
    } catch {
      setState('error');
    }
  }

  const photos = [
    { url: section.image_url, focal: section.image_focal },
    { url: section.second_image_url, focal: section.second_image_focal },
  ].filter((photo) => photo.url);
  const words = (
    <>
      <h2 className="text-2xl tracking-tight sm:text-3xl" style={STOREFRONT_HEADING_STYLE}>
        <Editable section={section} field="title" value={section.title} emphasis>
          <HeadingText>{section.title}</HeadingText>
        </Editable>
      </h2>
      {section.subtitle ? (
        <p className="mt-2 text-base" style={{ color: 'var(--storefront-muted)' }}>
          <Editable section={section} field="subtitle" value={section.subtitle}>
            {section.subtitle}
          </Editable>
        </p>
      ) : null}
    </>
  );
  const signup = (
    <>
      {state === 'done' ? (
        <p className="mt-6 text-sm font-semibold" style={{ color: 'var(--storefront-primary)' }}>
          {copy('section.newsletter_done')}
        </p>
      ) : (
        <form
          className={cn('mt-6 flex flex-col gap-3', section.collect_name ? 'sm:flex-row sm:flex-wrap' : 'sm:flex-row')}
          onSubmit={submit}
        >
          {section.collect_name ? (
            // Before the address, as a shopper reads a form. Optional: a
            // signup that refused an address without a name loses the address.
            <input
              type="text"
              name="name"
              autoComplete="name"
              maxLength={120}
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={copy('section.newsletter_name_placeholder')}
              aria-label={copy('section.field_name')}
              className="h-12 min-w-0 flex-1 border px-4 text-sm sm:min-w-[10rem]"
              style={NEWSLETTER_INPUT_STYLE}
            />
          ) : null}
          <input
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder={copy('section.newsletter_placeholder')}
            aria-label={copy('login.email')}
            className={cn('h-12 flex-1 border px-4 text-sm', section.collect_name && 'min-w-0 sm:min-w-[12rem]')}
            style={NEWSLETTER_INPUT_STYLE}
          />
          <button
            type="submit"
            disabled={state === 'sending'}
            className="h-12 shrink-0 px-6 text-sm font-semibold disabled:opacity-60"
            style={STOREFRONT_BUTTON_STYLE}
          >
            {state === 'sending' ? copy('product.adding') : section.button_label}
          </button>
        </form>
      )}
      {state === 'error' ? (
        <p className="mt-3 text-sm" style={{ color: 'var(--storefront-muted)' }}>
          {copy('section.newsletter_error')}
        </p>
      ) : null}
    </>
  );

  // Photos beside the form, when the merchant gave it some: one, or two side
  // by side. A side chosen with no photo yet keeps the centred form.
  if (section.image_side !== 'none' && photos.length > 0) {
    return (
      <Container>
        <div data-newsletter-photos={section.image_side} className="grid items-center gap-10 lg:grid-cols-2">
          <div
            className={cn(
              'grid gap-3',
              photos.length > 1 && 'grid-cols-2',
              section.image_side === 'right' && 'lg:order-2',
            )}
          >
            {photos.map(({ url, focal }) => (
              // Clips a repositioned photo's zoom to its frame.
              <div key={url} className="overflow-hidden" style={{ borderRadius: 'var(--storefront-radius)' }}>
                <StorefrontImage
                  url={url}
                  sizes="(min-width: 1024px) 25vw, 50vw"
                  alt=""
                  className={cn('w-full object-cover', photos.length > 1 ? 'aspect-[3/4]' : 'aspect-[4/3]')}
                  style={{ borderRadius: 'var(--storefront-radius)', ...focalStyle(focal) }}
                />
              </div>
            ))}
          </div>
          <div>
            {words}
            {signup}
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container width="tight" className="text-center">
      {words}
      {signup}
    </Container>
  );
}

/**
 * Bookings and appointments.
 *
 * Three steps down one page: what the business does, when it is free, and who
 * is asking. The services are the section's own blocks - so a merchant edits
 * them where they edit the words above them, and the customiser can outline
 * one the way it outlines a slide or a review. Everything after the choice of
 * service is `BookingCalendar`, which asks the server what is actually free.
 *
 * A service is selected on arrival rather than left blank. A shop with one
 * service - which is most of them at first - should show its times immediately
 * instead of asking a shopper to click the only option there is.
 *
 * **The page's own width, not the narrow one.** The other form sections here -
 * the contact form, the signup - ask for `narrow` because they are reading
 * matter with a field under it. This one is a wall of service cards and a grid
 * of twenty times, and both of those are scanned rather than read: at 48rem a
 * salon's eight services stack three-and-a-bit to a row inside two hand-widths
 * of empty page, and the times run six deep. The one part that *is* reading
 * matter is the form, and it keeps its own measure - see `BookingCalendar`.
 */
function Bookings({ section }: { section: BookingsSection }) {
  const { includeHidden, preview, team } = useEnvironment();
  const copy = useShopContent();
  // Narrowed to the merchant's picks from the service catalog - see `pickedBlocks`.
  const services = shownBlocks(pickedBlocks(section), includeHidden).filter((block) =>
    block.name.trim(),
  );
  // A plan card's `?service=` link starts the form on its service.
  const routed = useContext(UNSAFE_LocationContext) as { location: { search: string } } | null;
  const [selected, setSelected] = useState<string | null>(
    () => serviceFromSearch(services, routed?.location.search ?? null),
  );
  const chosen = services.find((service) => service.id === selected) ?? services[0] ?? null;

  if (services.length === 0) {
    return (
      <Container>
        <SectionHeading section={section} title={section.title} subtitle={section.subtitle} />
        <PreviewHint>Add a service before this section can take a booking.</PreviewHint>
      </Container>
    );
  }

  // Step by step, the service is the wizard's first question rather than a
  // wall of cards above it - see `BookingWizard`. Each card still goes through
  // `BlockFrame`, so the customiser outlines a service either way.
  if (section.form_layout === 'steps') {
    return (
      <Container>
        <SectionHeading section={section} title={section.title} subtitle={section.subtitle} />
        <BookingWizard
          section={section}
          services={services}
          preview={preview}
          team={team}
          frameService={(service, card) => (
            <BlockFrame section={section} block={service}>
              {card}
            </BlockFrame>
          )}
        />
      </Container>
    );
  }

  return (
    <Container>
      <SectionHeading section={section} title={section.title} subtitle={section.subtitle} />

      <div className={cn('grid gap-3', columnClass(section.columns))}>
        {services.map((service) => {
          const isChosen = chosen?.id === service.id;
          return (
            <BlockFrame key={service.id} section={section} block={service}>
              <button
                type="button"
                aria-pressed={isChosen}
                onClick={() => setSelected(service.id)}
                className={cn('h-full w-full border p-4 text-start', isChosen && 'font-semibold')}
                style={{
                  borderColor: isChosen
                    ? 'var(--storefront-primary)'
                    : 'var(--storefront-surface)',
                  borderRadius: 'var(--storefront-radius)',
                }}
              >
                {service.image_url ? (
                  // Clips a repositioned picture's zoom to its frame.
                  <span
                    className="mb-3 block overflow-hidden"
                    style={{ borderRadius: 'var(--storefront-radius)' }}
                  >
                    <StorefrontImage
                      url={service.image_url}
                      sizes={gridImageSizes(section.columns)}
                      alt=""
                      className={cn('aspect-[4/3] w-full', tileImageFit(service.image_focal))}
                      style={{
                        backgroundColor: 'var(--storefront-surface)',
                        borderRadius: 'var(--storefront-radius)',
                        ...focalStyle(service.image_focal),
                      }}
                    />
                  </span>
                ) : null}
                <span className="block text-sm">{service.name}</span>
                {/* The two facts that decide whether a shopper books: how long
                    it takes and what it costs. Both are the merchant's to hide
                    - a clinic that quotes on consultation has no price to
                    print, and printing "—" would be worse than printing
                    nothing. */}
                {section.show_duration || (section.show_price && service.price !== null) ? (
                  <span
                    className="mt-1 block text-xs font-normal"
                    style={{ color: 'var(--storefront-muted)' }}
                  >
                    {section.show_duration
                      ? copy('booking.duration', { minutes: service.duration_minutes })
                      : null}
                    {section.show_duration && section.show_price && service.price !== null
                      ? ' · '
                      : null}
                    {section.show_price && service.price !== null
                      ? formatMoney(service.price)
                      : null}
                  </span>
                ) : null}
                {service.description ? (
                  <span
                    className="mt-2 block text-xs font-normal leading-relaxed"
                    style={{ color: 'var(--storefront-muted)' }}
                  >
                    {service.description}
                  </span>
                ) : null}
              </button>
            </BlockFrame>
          );
        })}
      </div>

      {chosen ? (
        // Keyed by service, so switching one resets the day, the time and the
        // half-filled form together. Carrying Tuesday's ten o'clock across from
        // a forty-minute consultation to a ten-minute review would offer a slot
        // that service does not have.
        <BookingCalendar
          key={chosen.id}
          section={section}
          service={chosen}
          preview={preview}
          // The page's people, where the page fetched them (see
          // `useSectionData`); left unset, the calendar asks for them itself.
          team={team}
        />
      ) : null}
    </Container>
  );
}

/**
 * The language switcher, placed as a section (docs/i18n.md §7): a heading and
 * every language the shop publishes, named per the section's `display` or
 * else the theme's. Nothing on a shop with one language or with switching
 * turned off - except in the builder, where an invisible section would read as
 * a broken one, so the merchant sees a stand-in.
 */
function LanguageSelector({ section }: { section: LanguageSelectorSection }) {
  const { preview } = useEnvironment();
  const { languages, switcherEnabled } = useStorefrontLanguage();
  const theme = useThemeLanguageSwitcher();
  const display = section.display ?? theme.display;
  const live = switcherEnabled && languages.length > 1;

  if (!live && !preview) return null;

  return (
    <Container width="tight" className="text-center">
      {section.title ? (
        <h2 className="text-xl tracking-tight sm:text-2xl" style={STOREFRONT_HEADING_STYLE}>
          <HeadingText>{section.title}</HeadingText>
        </h2>
      ) : null}
      {live ? (
        <LanguageSwitcher variant="list" display={display} className="mt-4 justify-center" />
      ) : (
        <p aria-hidden className="mt-4 text-sm" style={{ color: 'var(--storefront-muted)' }}>
          English · हिन्दी · বাংলা
        </p>
      )}
    </Container>
  );
}

/**
 * The product's reviews. Everything but the heading is `ProductReviews`, which
 * owns the fetching, the form and the stand-ins; the heading is drawn here so
 * the customiser can still type into it.
 */
function ProductReviewsBand({ section }: { section: ProductReviewsSection }) {
  const { preview, product, storeName } = useEnvironment();
  return (
    <ProductReviews
      section={section}
      product={product ?? null}
      preview={preview}
      storeName={storeName}
      heading={<SectionHeading section={section} title={section.title} subtitle={section.subtitle} />}
    />
  );
}

/**
 * A sale's countdown. The clock is `CountdownView`; what is drawn here is the
 * wiring - copy typeable in the customiser, links inert there - and whether
 * this is a theme's static preview, which shows a demo clock for a section
 * with no end time yet.
 */
function CountdownBand({ section }: { section: CountdownSection }) {
  const { preview } = useEnvironment();
  const source = useStorefrontData();
  return (
    <CountdownView
      section={section}
      preview={preview}
      demo={source.preview}
      text={(field, value) => (
        <Editable
          section={section}
          field={field}
          value={value}
          emphasis={field === 'title'}
          isolateClick={field === 'cta_label'}
        >
          {field === 'title' ? <HeadingText>{value}</HeadingText> : value}
        </Editable>
      )}
      link={(href, className, style, children) => (
        <SectionLink href={href} className={className} style={style}>
          {children}
        </SectionLink>
      )}
    />
  );
}

/** A BMI form worked out in the shopper's browser - see `BmiCalculatorSection.tsx`. */
function BmiCalculatorBand({ section }: { section: BmiCalculatorSection }) {
  return (
    <Container>
      <BmiCalculatorView
        section={section}
        text={(field, value) => (
          <Editable
            section={section}
            field={field}
            value={value}
            emphasis={field === 'title'}
            multiline={field === 'note'}
            isolateClick={field === 'cta_label'}
          >
            {field === 'title' ? <HeadingText>{value}</HeadingText> : value}
          </Editable>
        )}
        link={(href, className, style, children) => (
          <SectionLink href={href} className={className} style={style}>
            {children}
          </SectionLink>
        )}
      />
    </Container>
  );
}

/** A band of short benefits scrolling sideways - see `MarqueeSection.tsx`. */
function MarqueeBand({ section }: { section: MarqueeSection }) {
  const { includeHidden, blockWrap } = useEnvironment();
  return (
    <MarqueeView
      section={section}
      includeHidden={includeHidden}
      icon={(name) => {
        const Icon = FEATURE_ICON_COMPONENTS[name];
        return <Icon className="size-[1.15em]" aria-hidden />;
      }}
      text={(block) => (
        <Editable section={section} field="text" blockId={block.id} value={block.text}>
          {block.text}
        </Editable>
      )}
      wrap={
        blockWrap
          ? (block, rendered) => (
              <BlockFrame section={section} block={block}>
                {rendered}
              </BlockFrame>
            )
          : undefined
      }
      emptyHint={
        <Container>
          <PreviewHint>Give an item some words to start this ribbon.</PreviewHint>
        </Container>
      }
    />
  );
}

/** The newest blog posts; the posts arrive through the environment (`useSectionData`). */
function BlogPostsBand({ section }: { section: BlogPostsSection }) {
  const { preview, blogTeaser } = useEnvironment();
  return (
    <BlogPostsView
      section={section}
      preview={preview}
      teaser={blogTeaser}
      heading={<SectionHeading section={section} title={section.title} subtitle={section.subtitle} />}
      link={(href, className, style, children) => (
        <SectionLink href={href} className={className} style={style}>
          {children}
        </SectionLink>
      )}
      ctaText={(value) => (
        <Editable section={section} field="cta_label" value={value} isolateClick>
          {value}
        </Editable>
      )}
    />
  );
}

/** A shoppable picture; its products come from the shop by slug. */
function LookbookBand({ section }: { section: LookbookSection }) {
  const { preview, includeHidden, productHref, productsFor, productDetailQuery, blockWrap } =
    useEnvironment();
  const { cart } = useStorefrontFeatures();
  const props = {
    section,
    preview,
    includeHidden,
    productHref,
    heading: <SectionHeading section={section} title={section.title} subtitle={section.subtitle} />,
    emptyHint: <PreviewHint>Add a picture to start this lookbook, then tag your products on it.</PreviewHint>,
    link: (href: string, className: string, children: ReactNode, style?: CSSProperties) => (
      <SectionLink href={href} className={className} style={style}>
        {children}
      </SectionLink>
    ),
    wrap: blockWrap
      ? (block: Block, rendered: ReactNode) => (
          <BlockFrame section={section} block={block}>
            {rendered}
          </BlockFrame>
        )
      : undefined,
  };
  const summaries = productsFor(null);
  if (productDetailQuery) {
    return (
      <Container>
        <FetchedLookbook {...props} query={productDetailQuery} quickAdd={cart && !preview} />
      </Container>
    );
  }
  // No way to fetch a product in full: the catalog's summaries answer, by
  // slug or by id, and the cards link only.
  const products: Record<string, LookbookProduct> = {};
  for (const product of summaries) {
    const card = lookbookProductFromSummary(product);
    products[product.slug] = card;
    products[product.id] = card;
  }
  return (
    <Container>
      <LookbookView {...props} products={products} />
    </Container>
  );
}

/** Plans side by side, each a card of the merchant's own words and a button. */
function PricingPlansBand({ section }: { section: PricingPlansSection }) {
  const { includeHidden, blockWrap, productHref, preview } = useEnvironment();
  // A plan that sells a session package buys it - on the live shop, inside a
  // router and with an account to buy into. Anywhere else it stays a link.
  const routed = useInRouterContext();
  const account = useStorefrontAccountData();
  const sells = !preview && routed && Boolean(account.buyPackage);
  return (
    <Container>
      <PricingPlansView
        section={section}
        includeHidden={includeHidden}
        productHref={productHref}
        packageButton={
          sells
            ? (block, className, children, style) => (
                <PackageBuyButton
                  packageId={block.package_id!}
                  planName={block.name}
                  className={className}
                  style={style}
                >
                  {children}
                </PackageBuyButton>
              )
            : undefined
        }
        heading={
          // The intro: a small line over the title, the title, a paragraph and
          // a button - each only when the merchant wrote one.
          <div className={section.layout === 'intro_left' ? undefined : 'mb-10'}>
            {section.eyebrow ? (
              <p
                className="mb-3 text-xs font-semibold uppercase tracking-widest"
                style={{ color: 'var(--storefront-primary)' }}
              >
                <Editable section={section} field="eyebrow" value={section.eyebrow}>
                  {section.eyebrow}
                </Editable>
              </p>
            ) : null}
            <SectionHeading section={section} title={section.title} subtitle={section.subtitle} className="mb-0" />
            {section.body ? (
              <p className="mt-4 max-w-xl text-base leading-relaxed" style={{ color: 'var(--storefront-muted)' }}>
                <Editable section={section} field="body" value={section.body}>
                  {section.body}
                </Editable>
              </p>
            ) : null}
            <CtaButton label={section.cta_label} href={section.cta_href || '/products'} section={section} />
          </div>
        }
        link={(href, className, children, style) => (
          <SectionLink href={href} className={className} style={style}>
            {children}
          </SectionLink>
        )}
        wrap={
          blockWrap
            ? (block, rendered) => (
                <BlockFrame section={section} block={block}>
                  {rendered}
                </BlockFrame>
              )
            : undefined
        }
        emptyHint={<PreviewHint>Give a plan a name to show it here.</PreviewHint>}
      />
    </Container>
  );
}

/** One product shown large; its full record fetched where the page can. */
function ProductSpotlightBand({ section }: { section: ProductSpotlightSection }) {
  const { preview, productHref, productsFor, productDetailQuery } = useEnvironment();
  const { cart } = useStorefrontFeatures();
  const summaries = productsFor(null);
  const slug = spotlightSlug(section, summaries);
  const found = slug ? summaries.find((each) => each.slug === slug || each.id === slug) : undefined;
  const summary = found ? spotlightFromSummary(found) : null;
  const props = {
    section,
    productHref,
    link: (href: string, className: string, children: ReactNode, style?: CSSProperties) => (
      <SectionLink href={href} className={className} style={style}>
        {children}
      </SectionLink>
    ),
    eyebrow: section.eyebrow ? (
      <Editable section={section} field="eyebrow" value={section.eyebrow}>
        {section.eyebrow}
      </Editable>
    ) : undefined,
    // Nothing to show on the shop - a picked product since deleted - takes
    // the band with it; the customiser says what is missing instead.
    emptyHint: preview ? (
      <PreviewHint>Pick a product to show it here.</PreviewHint>
    ) : (
      <div data-section-empty hidden />
    ),
  };
  if (!slug) {
    return <Container>{props.emptyHint}</Container>;
  }
  return (
    <Container>
      {productDetailQuery ? (
        <FetchedProductSpotlight
          {...props}
          query={productDetailQuery}
          slug={slug}
          summary={summary}
          quickAdd={cart && !preview}
        />
      ) : (
        <ProductSpotlightView {...props} product={summary} />
      )}
    </Container>
  );
}

/**
 * A dark band with a big product scene, words and a button, and a small card
 * for one product on the scene - see `ProductShowcaseSection.tsx`.
 */
/**
 * The slider showcase: the pills pick a category, whose newest products the
 * slider pages through - fetched by the tab query where the page has one, the
 * catalog's own first page otherwise (and with no pills at all).
 */
/** How few products a showcase slider shows before it borrows the shop's newest. */
const SHOWCASE_MIN_SLIDES = 3;

function ShowcaseCarouselBand({ section, fill }: { section: ProductShowcaseSection; fill: string }) {
  const { preview, productHref, productsFor, productQuery, categories } = useEnvironment();
  const pills = showcasePillCategories(section, categories);
  const [picked, setPicked] = useState<string | null>(null);
  const active = pills.some((pill) => pill.id === picked) ? picked : (pills[0]?.id ?? null);
  const query = productQuery && active ? productQuery({ sort: 'newest', onSale: false, categoryId: active, size: 8 }) : null;
  const fetched = useQuery({
    queryKey: query?.queryKey ?? ['showcase-carousel', 'none'],
    queryFn: query?.queryFn ?? (async () => []),
    enabled: Boolean(query),
  });
  // The category's own products first; a category with too few to page
  // through is topped up with the shop's newest, so the arrows have work.
  // Asked for here: a page with no product grid has not fetched them.
  const own = query ? (fetched.data ?? []) : productsFor(null);
  const topUp = productQuery ? productQuery({ sort: 'newest', onSale: false, categoryId: null, size: 8 }) : null;
  const newestQuery = useQuery({
    queryKey: topUp?.queryKey ?? ['showcase-carousel', 'newest', 'none'],
    queryFn: topUp?.queryFn ?? (async () => []),
    enabled: Boolean(topUp) && !fetched.isPending && own.length < SHOWCASE_MIN_SLIDES,
  });
  const newest = newestQuery.data ?? productsFor(null);
  const summaries =
    own.length >= SHOWCASE_MIN_SLIDES
      ? own
      : [...own, ...newest.filter((each) => !own.some((mine) => mine.id === each.id))].slice(0, 8);
  const link = (href: string, className: string, children: ReactNode, style?: CSSProperties) => (
    <SectionLink href={href} className={className} style={style}>
      {children}
    </SectionLink>
  );
  return (
    <Container>
      <ProductShowcaseCarousel
        section={section}
        fill={fill}
        preview={preview}
        heading={
          <>
            {section.eyebrow ? (
              <p
                className="mb-2 text-xs font-semibold uppercase tracking-widest"
                style={{ color: 'var(--storefront-primary)' }}
              >
                <Editable section={section} field="eyebrow" value={section.eyebrow}>
                  {section.eyebrow}
                </Editable>
              </p>
            ) : null}
            {section.heading ? (
              <h2 className="text-3xl tracking-tight sm:text-4xl" style={STOREFRONT_HEADING_STYLE}>
                <Editable section={section} field="heading" value={section.heading} emphasis>
                  <HeadingText>{section.heading}</HeadingText>
                </Editable>
              </h2>
            ) : null}
          </>
        }
        cta={
          section.cta_label ? (
            <CtaButton label={section.cta_label} href={section.cta_href || '/products'} section={section} />
          ) : undefined
        }
        categories={pills}
        activeCategory={active}
        onPickCategory={setPicked}
        products={summaries.map(spotlightFromSummary)}
        loading={Boolean(query) && fetched.isPending}
        productHref={productHref}
        link={link}
      />
    </Container>
  );
}

function ProductShowcaseBand({ section }: { section: ProductShowcaseSection }) {
  const panel = showcasePanelFill(section);
  if (panel) return <ShowcaseCarouselBand section={section} fill={panel} />;
  return <ProductShowcaseWords section={section} />;
}

function ProductShowcaseWords({ section }: { section: ProductShowcaseSection }) {
  const { preview, productHref, productsFor, productDetailQuery, categories } = useEnvironment();
  const { cart } = useStorefrontFeatures();
  const summaries = productsFor(null);
  const slug = section.show_card ? spotlightSlug(section, summaries) : null;
  const found = slug ? summaries.find((each) => each.slug === slug || each.id === slug) : undefined;
  const summary = found ? spotlightFromSummary(found) : null;
  const words = (
    <>
      {section.eyebrow ? (
        <p
          className="mb-3 text-xs font-semibold uppercase tracking-widest"
          style={{ color: 'var(--storefront-primary)' }}
        >
          <Editable section={section} field="eyebrow" value={section.eyebrow}>
            {section.eyebrow}
          </Editable>
        </p>
      ) : null}
      {section.heading ? (
        <h2 className="text-3xl tracking-tight sm:text-4xl" style={STOREFRONT_HEADING_STYLE}>
          <Editable section={section} field="heading" value={section.heading} emphasis>
            <HeadingText>{section.heading}</HeadingText>
          </Editable>
        </h2>
      ) : null}
      {section.body ? (
        <p
          className="mt-4 max-w-xl whitespace-pre-line text-base leading-relaxed"
          style={{ color: 'var(--storefront-muted)' }}
        >
          <Editable section={section} field="body" value={section.body} multiline>
            {section.body}
          </Editable>
        </p>
      ) : null}
      {/* Blank sends the button to the catalog. */}
      <CtaButton label={section.cta_label} href={section.cta_href || '/products'} section={section} />
    </>
  );
  const props = {
    section,
    preview,
    productHref,
    words,
    link: (href: string, className: string, children: ReactNode, style?: CSSProperties) => (
      <SectionLink href={href} className={className} style={style}>
        {children}
      </SectionLink>
    ),
  };
  return (
    <Container>
      <ShowcasePills
        categories={showcasePillCategories(section, categories)}
        pillStyle={section.pill_style}
        link={props.link}
      />
      {slug && productDetailQuery ? (
        <FetchedProductShowcase
          {...props}
          query={productDetailQuery}
          slug={slug}
          summary={summary}
          quickAdd={cart && !preview}
        />
      ) : (
        <ProductShowcaseView {...props} product={summary} />
      )}
    </Container>
  );
}

/** Two photos under a draggable divider. */
/** Two photo cards across a phone, four from the large breakpoint. */
const BEFORE_AFTER_ACROSS = '[--rail-across:2] lg:[--rail-across:4] [--rail-gap:1rem] lg:[--rail-gap:1.25rem]';

function BeforeAfterBand({ section }: { section: BeforeAfterSection }) {
  const { preview } = useEnvironment();
  if (section.layout === 'cards') {
    return (
      <Container>
        <BeforeAfterCards
          section={section}
          rail={(items, count) => (
            <Rail
              label={section.title || section.before_label || 'Before and after'}
              itemCount={count}
              across={BEFORE_AFTER_ACROSS}
              heading={
                <SectionHeading section={section} title={section.title} subtitle={section.subtitle} className="mb-4" />
              }
            >
              {items.map((item, index) => (
                <li key={index} className={RAIL_ITEM}>
                  {item}
                </li>
              ))}
            </Rail>
          )}
        />
      </Container>
    );
  }
  const missing = !section.before_image_url
    ? !section.after_image_url
      ? 'Add a before and an after photo to start this comparison.'
      : 'Add the before photo to finish this comparison.'
    : 'Add the after photo to finish this comparison.';
  return (
    <Container>
      <BeforeAfterView
        section={section}
        preview={preview}
        heading={<SectionHeading section={section} title={section.title} subtitle={section.subtitle} />}
        emptyHint={<PreviewHint>{missing}</PreviewHint>}
      />
    </Container>
  );
}

/** Where the dashboard keeps the branches a store locator lists. */
const STORE_LOCATIONS_SCREEN = '/app/store-locations';

/** The shop's branches - see `StoreLocatorSection.tsx`. */
function StoreLocatorBand({ section }: { section: StoreLocatorSection }) {
  const { preview, storeLocations } = useEnvironment();
  return (
    <StoreLocatorView
      section={section}
      // A renderer given no list at all (a caller that never fetched one) has
      // nothing to wait for: it reads as no branches.
      data={storeLocations === undefined ? { timezone: 'Asia/Kolkata', locations: [] } : storeLocations}
      preview={preview}
      heading={<SectionHeading section={section} title={section.title} subtitle={section.subtitle} />}
      emptyHint={
        <PreviewHint>
          Add your shops on the{' '}
          <a
            href={STORE_LOCATIONS_SCREEN}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold underline"
            data-store-locations-link
          >
            Store locations
          </a>{' '}
          screen and they appear here. Until then this section is hidden on your shop.
        </PreviewHint>
      }
    />
  );
}

/** Where the dashboard keeps the people a team section lists. */
const TEAM_SCREEN = '/app/our-team';

/** Three drawn stand-ins, for a customiser page whose shop has nobody yet. */
const STAND_IN_TEAM = placeholderTeam(3);

/**
 * The shop's people - see `TeamSection.tsx`.
 *
 * In the customiser with stand-ins switched on, a shop with nobody yet draws
 * three drawn silhouettes under the hint, so the merchant can judge columns
 * and card styles before adding anyone. The live shop never does.
 */
function TeamBand({ section }: { section: TeamSection }) {
  const { preview, placeholders, team } = useEnvironment();
  const empty = team !== null && team !== undefined && team.members.length === 0;
  const standIn = Boolean(preview && placeholders && (team === undefined || empty));
  const hint = (
    <PreviewHint>
      Add your people on the{' '}
      <a
        href={TEAM_SCREEN}
        target="_blank"
        rel="noopener noreferrer"
        className="font-semibold underline"
        data-team-screen-link
      >
        Team
      </a>{' '}
      screen and they appear here. Until then this section is hidden on your shop.
    </PreviewHint>
  );
  return (
    <>
      {standIn ? <Container>{hint}</Container> : null}
      <TeamView
        section={section}
        // A renderer given no list at all (a caller that never fetched one)
        // has nothing to wait for: it reads as nobody.
        team={standIn ? STAND_IN_TEAM : team === undefined ? NOBODY : team}
        preview={preview}
        heading={<SectionHeading section={section} title={section.title} subtitle={section.subtitle} />}
        emptyHint={hint}
        link={(href, className, children, style) => (
          <SectionLink href={href} className={className} style={style}>
            {children}
          </SectionLink>
        )}
      />
    </>
  );
}

const NOBODY: StorefrontTeam = { singular: 'Team member', plural: 'Team', members: [] };

/** Where the dashboard keeps the roles a careers section lists. */
const CAREERS_SCREEN = '/app/careers';

/** The shop's newest open positions - see `CareersSection.tsx`. */
function CareersBand({ section }: { section: CareersSection }) {
  const { preview, careers } = useEnvironment();
  const features = useStorefrontFeatures();
  return (
    <CareersView
      section={section}
      // A renderer given no list at all (a caller that never fetched one) has
      // nothing to wait for: it reads as no openings.
      careers={careers === undefined ? { openings: [] } : careers}
      enabled={features.careers}
      preview={preview}
      heading={<SectionHeading section={section} title={section.title} subtitle={section.subtitle} />}
      emptyHint={
        <PreviewHint>
          Add your open roles on the{' '}
          <a
            href={CAREERS_SCREEN}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold underline"
            data-careers-screen-link
          >
            Careers
          </a>{' '}
          screen and turn on your Careers page, and they appear here. Until then this section is hidden on
          your shop.
        </PreviewHint>
      }
      link={(href, className, children, style) => (
        <SectionLink href={href} className={className} style={style}>
          {children}
        </SectionLink>
      )}
      ctaText={(value) => (
        <Editable section={section} field="cta_label" value={value} isolateClick>
          {value}
        </Editable>
      )}
    />
  );
}

// --- Dispatch -------------------------------------------------------------
/**
 * A page's own content, wherever the merchant left it in the stack.
 *
 * Reads its slot and renders it. There is nothing else to do: the settings on
 * an anchor are read by the page that fills the slot, not here, because they
 * describe a grid and a set of filters that only that page can apply.
 */
function Anchor({ section }: { section: AnchorSection }) {
  const { slots } = useEnvironment();
  return <>{slots?.[section.type] ?? null}</>;
}

function SectionBody({ section }: { section: Section }) {
  if (isAnchor(section)) return <Anchor section={section} />;

  switch (section.type) {
    case 'hero':
      return <Hero section={section} />;
    case 'slideshow':
      return <Slideshow section={section} />;
    case 'featured_products':
      return <FeaturedProducts section={section} />;
    case 'recently_viewed':
      return <RecentlyViewed section={section} />;
    case 'product_tabs':
      return <ProductTabs section={section} />;
    case 'categories':
      return <Categories section={section} />;
    case 'category_strip':
      return <CategoryStrip section={section} />;
    case 'image_with_text':
      return <ImageWithText section={section} />;
    case 'banner':
      return <Banner section={section} />;
    case 'text':
      return <Text section={section} />;
    case 'link_directory':
      return <LinkDirectoryBand section={section} />;
    case 'features':
      return <Features section={section} />;
    case 'testimonials':
      return <Testimonials section={section} />;
    case 'faq':
      return <Faq section={section} />;
    case 'timeline':
      return <Timeline section={section} />;
    case 'gallery':
      return <Gallery section={section} />;
    case 'promo_grid':
      return <PromoGrid section={section} />;
    case 'brand_strip':
      return <BrandStrip section={section} />;
    case 'lookbook':
      return <LookbookBand section={section} />;
    case 'before_after':
      return <BeforeAfterBand section={section} />;
    case 'pricing_plans':
      return <PricingPlansBand section={section} />;
    case 'product_spotlight':
      return <ProductSpotlightBand section={section} />;
    case 'product_showcase':
      return <ProductShowcaseBand section={section} />;
    case 'video':
      return <Video section={section} />;
    case 'social_links':
      return <SocialLinks section={section} />;
    case 'contact_form':
      return <ContactForm section={section} />;
    case 'newsletter':
      return <Newsletter section={section} />;
    case 'countdown':
      return <CountdownBand section={section} />;
    case 'bmi_calculator':
      return <BmiCalculatorBand section={section} />;
    case 'marquee':
      return <MarqueeBand section={section} />;
    case 'blog_posts':
      return <BlogPostsBand section={section} />;
    case 'bookings':
      return <Bookings section={section} />;
    case 'store_locator':
      return <StoreLocatorBand section={section} />;
    case 'team':
      return <TeamBand section={section} />;
    case 'careers':
      return <CareersBand section={section} />;
    case 'product_reviews':
      return <ProductReviewsBand section={section} />;
    case 'language_selector':
      return <LanguageSelector section={section} />;
  }
}

const BACKGROUNDS: Record<Section['background'], string | undefined> = {
  default: undefined,
  surface: 'var(--storefront-surface)',
  primary: 'var(--storefront-primary)',
};

/**
 * What a band is painted in, for the edge drawn in it: the merchant's own
 * colour, else the preset's, else the page - a default band is the page.
 */
function edgeFill(section: Section): string {
  return (
    resolveColor(section.background_color) ??
    BACKGROUNDS[section.background] ??
    'var(--storefront-background)'
  );
}

export interface SectionViewProps {
  section: Section;
  /** Wraps the section, for the builder's selection outline. */
  className?: string;
}

/**
 * The section's vertical padding, as classes.
 *
 * Three shapes, in order of how common they are: a banner that bleeds and has
 * none; a section nobody has given a per-device padding, which renders the
 * single class it has always rendered; and one that has been given one, which
 * spells out all three widths so each holds across its own band rather than
 * inheriting half of the one above it.
 *
 * A caller that already knows the device - the customiser's preview frame,
 * which is narrow inside a wide window - gets the one resolved answer instead,
 * because a media query there would report the merchant's monitor.
 */
function paddingClass(section: Section, device: SectionDevice | undefined): string {
  if (sectionBleeds(section)) return 'py-0';
  if (device) return PADDING[sectionPadding(section, device)];
  if (section.padding_tablet === null && section.padding_mobile === null) {
    return PADDING[section.padding];
  }
  return cn(
    PADDING_MOBILE[sectionPadding(section, 'mobile')],
    PADDING_TABLET[sectionPadding(section, 'tablet')],
    PADDING_DESKTOP[section.padding],
  );
}

/** One section with its own padding and background applied. */
/**
 * The merchant's content width, handed to every `Container` inside the
 * section as `--section-content-max`.
 *
 * In the customiser the screen is known, so the one value is set. On the
 * shopper's page it is not, so both are set and the breakpoint picks - a
 * width left on Fit is simply not set, and the container falls back to the
 * section's own width. The phone's breakpoint is `HIDDEN_ON`'s, so "the
 * phone" means the same screens in both settings.
 */
function contentWidthStyle(section: Section, device: SectionDevice | undefined): CSSProperties {
  const style: Record<string, string> = {};
  if (device) {
    const percent = contentWidthFor(section, device);
    if (percent !== null) style['--section-content-max'] = contentWidthLength(percent);
    return style as CSSProperties;
  }
  const desktop = contentWidthFor(section, 'desktop');
  const mobile = contentWidthFor(section, 'mobile');
  if (desktop !== null) style['--section-width-desktop'] = contentWidthLength(desktop);
  if (mobile !== null) style['--section-width-mobile'] = contentWidthLength(mobile);
  return style as CSSProperties;
}

/** The breakpoint switch for `contentWidthStyle`, only on a section that set a width. */
function contentWidthClass(section: Section): string | undefined {
  const desktop = contentWidthFor(section, 'desktop');
  const mobile = contentWidthFor(section, 'mobile');
  if (desktop === null && mobile === null) return undefined;
  return 'md:[--section-content-max:var(--section-width-desktop)] max-md:[--section-content-max:var(--section-width-mobile)]';
}

/**
 * Whether a section paints a band of its own colour (or photograph) - the
 * sections a card theme keeps to the page column. A banner that bleeds asked
 * for the window, an edged one is shaped against it, and a contained split
 * hero is already its own card.
 */
function paintsBand(
  section: Section,
  preview: boolean | undefined,
  edged: boolean,
  pageBackground: string | undefined,
): boolean {
  if (edged || sectionBleeds(section) || paintsOwnCard(section)) return false;
  if (faqPhotoBehind(section, preview) || section.background !== 'default') return true;
  const own = section.background_color;
  if (resolveColor(own) === undefined) return false;
  // A colour that *is* the page's paints nothing to frame - carding it would
  // only pull its content in from the column's edges.
  return !(
    own === 'background' ||
    (pageBackground !== undefined && own?.trim().toLowerCase() === pageBackground.trim().toLowerCase())
  );
}

export function SectionView({ section: given, className }: SectionViewProps) {
  const { device, placeholders, preview } = useEnvironment();
  const shellTheme = useContext(StorefrontThemeContext);

  // Filled in here rather than inside each section component, because the two
  // things computed below - whether the band bleeds to the window edges, and
  // how much padding it takes - both read the content. A hero that has been
  // given a stand-in photograph is a full-bleed hero, and it has to be drawn as
  // one or the merchant is judging a layout they will not get.
  // In the colour mode plugin's other mode, the merchant's own hex colours on
  // the band and its blocks are carried across too - a white band on a light
  // shop is a dark band on a dark one. Palette picks follow by themselves.
  const { flipped } = useColorMode();
  const section = useMemo(() => {
    const filled = placeholders ? withPlaceholderContent(given) : given;
    return flipped ? flipColors(filled) : filled;
  }, [given, placeholders, flipped]);
  // A wavy or curved edge needs the band to be the box it is placed against.
  const edged = section.edge_top !== 'straight' || section.edge_bottom !== 'straight';

  return (
    <section
      id={`section-${section.id}`}
      className={cn(
        paddingClass(section, device),
        // Left to the breakpoints on the shopper's page. In the customiser the
        // device is already known, and the list dims rather than drops - so a
        // merchant designing the phone can still see what stands where.
        device ? undefined : section.hidden_on.map((each) => HIDDEN_ON[each]),
        device ? undefined : contentWidthClass(section),
        // A section that found it has nothing to show after all - a product
        // tab row whose every tab came back empty - takes its band with it.
        'has-[[data-section-empty]]:hidden',
        paintsBand(section, preview, edged, shellTheme?.colors.background) && BAND_CARD,
        className,
      )}
      style={{
        backgroundColor: BACKGROUNDS[section.background],
        // Text on the primary colour has to flip, or a dark brand colour eats
        // the copy sitting on it.
        color: section.background === 'primary' ? 'var(--storefront-background)' : undefined,
        // The merchant's own colours, line and exact padding, over the presets.
        ...areaStyle(section, { bleeds: sectionBleeds(section) }),
        // A contained split hero paints its colour as a card in the column
        // (`heroCardFill`); the band keeps the colour's variables for the
        // copy on it, but not the paint.
        ...(paintsOwnCard(section) ? { backgroundColor: undefined } : {}),
        ...contentWidthStyle(section, device),
        // A wavy or curved edge is drawn just outside the band; see `SectionEdge`.
        // So is an FAQ whose photo fills the band behind its questions.
        ...(edged || faqPhotoBehind(section, preview) ? { position: 'relative' } : {}),
      }}
    >
      {edged ? <SectionEdge shape={section.edge_top} side="top" fill={edgeFill(section)} /> : null}
      <SectionBody section={section} />
      {edged ? <SectionEdge shape={section.edge_bottom} side="bottom" fill={edgeFill(section)} /> : null}
    </section>
  );
}

export interface SectionListProps extends SectionEnvironment {
  sections: Section[];
  /** Renders hidden sections too, dimmed - the builder shows what it can edit. */
  includeHidden?: boolean;
  /** Wraps each section, so the builder can outline the selected one. */
  wrap?: (section: Section, rendered: ReactNode) => ReactNode;
}

/**
 * Whether a section has nothing to render yet.
 *
 * A half-finished section is a blank band of padding on the live storefront, so
 * it is left out there entirely - while the builder keeps showing it, because
 * that is where the merchant is going to finish it.
 */
function isUnfinished(
  section: Section,
  environment?: SectionEnvironment,
  shop: { demo: boolean; blog: boolean } = { demo: false, blog: true },
): boolean {
  // An anchor is the page. It is never held back for being half-written, and
  // an empty cart or a catalog with nothing in it says so in its own words.
  if (isAnchor(section)) return false;

  if (section.type === 'countdown') {
    // No end time is "not set up yet" - except in a theme's static preview,
    // which draws a demo clock. A sale that has ended and hides comes off too.
    const endsAt = parseEndsAt(section.ends_at);
    if (endsAt === null) return !shop.demo;
    return section.expired_behavior === 'hide' && endsAt <= Date.now();
  }
  if (section.type === 'blog_posts') {
    // No posts to show, or a shop whose blog is switched off.
    return (
      !shop.blog ||
      teaserPosts(environment?.blogTeaser ?? EMPTY_TEASER, section.blog, section.limit).length === 0
    );
  }

  // Read through the visible blocks, not all of them: a slideshow whose only
  // slide the merchant hid is as empty on the shopper's page as one that never
  // had a slide, and both should make the page shorter rather than leave a
  // band of padding with nothing in it.
  switch (section.type) {
    case 'hero':
      // A hero the merchant added and never wrote or pictured is the
      // builder's prompt ("A headline that says what you sell") on a blank
      // band - on a privacy policy, above the policy.
      return isPlaceholderHero(section) && heroImageItems(section).length === 0;
    case 'slideshow':
      return shownBlocks(section.blocks).length === 0;
    case 'features':
      return shownBlocks(section.blocks).length === 0;
    case 'gallery':
      return shownBlocks(section.blocks).every((block) => !block.image_url);
    case 'promo_grid':
      // A tile with no picture is still a coloured block with words on it.
      return shownBlocks(section.blocks).length === 0;
    case 'brand_strip':
      // Off the page when there is neither a logo, a typed brand nor a brand
      // on any product to fall back to.
      return brandStripItems(section, environment?.brands ?? []).length === 0;
    case 'category_strip':
      // A thin row under the header saying "no categories yet" is noise on
      // the live shop; the customiser keeps it, with its empty line.
      return categoryStripItems(section, environment?.categories ?? []).length === 0;
    case 'lookbook':
      // Hotspots are drawn over the picture, so without one there is nothing.
      return !section.image_url;
    case 'before_after':
      // Photo cards stand with any photo - or, in a theme's sample, as empty
      // frames to judge the layout by. Half a comparison compares nothing.
      if (section.layout === 'cards') return !hasBeforeAfterPhoto(section) && !shop.demo;
      return !section.before_image_url || !section.after_image_url;
    case 'pricing_plans':
      // A plan with no name is a card with nothing to choose.
      return pricingPlans(section).length === 0;
    case 'marquee':
      // A ribbon with no words is a coloured stripe.
      return marqueeItems(section).length === 0;
    case 'product_spotlight':
      // Neither a picked product nor any product to stand in.
      return spotlightSlug(section, environment?.productsFor(null) ?? []) === null;
    case 'product_showcase':
      // Words with no heading and no scene are a dark band of nothing much.
      return !section.heading.trim() && !section.image_url;
    case 'testimonials':
      return shownBlocks(section.blocks).every((block) => !block.quote.trim());
    case 'faq':
      return shownBlocks(pickedBlocks(section)).every((block) => !block.question.trim());
    case 'timeline':
      return shownBlocks(section.blocks).every((block) => !block.title.trim());
    case 'bookings':
      // A booking form offering nothing to book is a dead end for a
      // shopper, not a shorter page - so it comes off the live page too.
      return shownBlocks(pickedBlocks(section)).every((block) => !block.name.trim());
    case 'product_reviews':
      // Reviews of no product in particular are not reviews. Off the page
      // wherever the page has no product - a custom page, the home page - and
      // the section says for itself when the shop's plan has none.
      return !environment?.product;
    case 'text':
      return !section.body.trim();
    case 'link_directory':
      return linkDirectoryGroups(section, environment?.categories ?? []).length === 0;
    case 'social_links':
      // The common case, not an edge one: a merchant adds the section, fills
      // in three of the seven rows and never touches the rest. A section where
      // they filled in none of them is a band of padding on the live page, so
      // it comes off it - and stays in the customiser, where the empty rows are
      // the prompt.
      return shownBlocks(section.blocks).every(
        (block) => !socialLinkHref(block.platform, block.url),
      );
    case 'video':
      // A band of padding where a video was meant to be is worse than a
      // shorter page, and the merchant sees the prompt in the customiser
      // either way.
      return section.source === 'youtube'
        ? !youtubeVideoId(section.youtube_url)
        : !section.video_url.trim();
    default:
      // Everything else renders from its own settings, or says for itself that
      // the catalog behind it is still empty.
      return false;
  }
}

/**
 * The share of the row one column takes, above the breakpoint.
 *
 * Written out rather than computed so Tailwind can see the classes, and only
 * from `md:` up: two half-width sections on a phone are two columns of about
 * 160 pixels each, which is narrower than the form fields inside them. Below
 * the breakpoint a row is the stack it would have been anyway.
 */
const SPAN_WIDTHS: Record<SectionSpan, string> = {
  full: 'md:w-full',
  two_thirds: 'md:w-2/3',
  half: 'md:w-1/2',
  third: 'md:w-1/3',
};

/**
 * The stand-in catalog, built once.
 *
 * Once rather than per render because these are handed to the environment: a
 * fresh array every keystroke would re-render every product card on the page
 * while the merchant types in a heading three sections above them. Twelve is
 * the largest grid a section can ask for - six columns, two rows - so the
 * slice each section takes is always full.
 */
const PLACEHOLDER_PRODUCTS = placeholderProducts(12);
const PLACEHOLDER_CATEGORIES = placeholderCategories(6);

/** The whole page, in order. */
export function SectionList({
  sections,
  includeHidden = false,
  wrap,
  ...environment
}: SectionListProps) {
  const source = useStorefrontData();
  const features = useStorefrontFeatures();
  const shop = { demo: source.preview, blog: features.blog };
  const shown = (includeHidden ? sections : sections.filter((section) => section.visible)).filter(
    (section) => environment.preview || !isUnfinished(section, environment, shop),
  );

  // The catalog, with stand-ins behind it where the merchant's own is still
  // empty. Here rather than inside the two sections that read it, because both
  // of them ask through the environment and neither should have to learn what a
  // placeholder is. A shop that *has* products never reaches the fallback, and
  // a section pinned to a category the merchant has not filled gets the same
  // stand-ins as an unpinned one - which is right: what they are judging is the
  // grid, not the catalog.
  const filled: SectionEnvironment = environment.placeholders
    ? {
        ...environment,
        productsFor: (categoryId) => {
          const real = environment.productsFor(categoryId);
          return real.length > 0 ? real : PLACEHOLDER_PRODUCTS;
        },
        categories: environment.categories.length > 0 ? environment.categories : PLACEHOLDER_CATEGORIES,
        // Nothing is pending once there is something to draw, and a stand-in
        // grid that pulsed like a skeleton would undo the point of drawing it.
        loading: false,
      }
    : environment;

  // Dimmed rather than dropped, and for two reasons now: the merchant hid it,
  // or they left it off the screen size being previewed. Both are things they
  // did on purpose and both are things they need to see the position of.
  const dimmed = (section: Section) =>
    !section.visible ||
    (environment.device !== undefined && section.hidden_on.includes(environment.device));

  const render = (section: Section) => {
    const rendered = (
      <SectionView
        section={section}
        className={includeHidden && dimmed(section) ? 'opacity-40' : undefined}
      />
    );
    return wrap ? wrap(section, rendered) : rendered;
  };

  return (
    <EnvironmentContext.Provider value={{ ...filled, includeHidden }}>
      {sectionRows(shown).map((row, index) => (
        // The first row is what a shopper sees on arrival, so its pictures
        // load at once; every row below waits until it is scrolled near.
        <AboveTheFold key={row[0].id} value={index === 0}>
          {/* A full-width section is rendered exactly as it always was, with no
              wrapper around it: the hero's artwork bleeds to the window edges,
              and a page of ordinary sections produces the markup it produced
              before rows existed. */}
          {row.length === 1 && sectionSpan(row[0]) === 'full' ? (
            render(row[0])
          ) : (
            <div
              // The row is what holds the page column now, so the sections in it
              // line up with the full-width ones above and below. Each section
              // keeps its own container inside, whose side padding becomes the
              // gutter between the columns.
              className="mx-auto flex w-full flex-col md:flex-row md:items-stretch md:justify-center"
              style={{ maxWidth: 'var(--storefront-container-max, 72rem)' }}
            >
              {row.map((section) => (
                <div key={section.id} className={cn('min-w-0', SPAN_WIDTHS[sectionSpan(section)])}>
                  {render(section)}
                </div>
              ))}
            </div>
          )}
        </AboveTheFold>
      ))}
    </EnvironmentContext.Provider>
  );
}
